// Sidebar.js — approvazioni reali su Firestore users{status:'pending'}, badge pendenti,
// upload con destinazione/cartella nuova con icona/visibilità, link statici spostabili,
// cartelle custom con icona, DnD/sposta, rinomina da dialog, niente "Uploads" legacy,
// pulsanti header in griglia 5 per riga (forzata), altezza dinamica a 100dvh.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// Firebase
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// MUI
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  LinearProgress,
  Badge,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  RadioGroup,
  Radio,
  Box,
  Typography,
} from '@mui/material';

// Icons
import {
  ExpandLess,
  ExpandMore,
  MoreVert,
  CloudUpload,
  DeleteOutline,
  OpenInNew,
  Search as SearchIcon,
  Edit as EditIcon,
  DriveFileMove as MoveIcon,
  Logout,
  Folder as FolderIcon,
  CreateNewFolder as CreateFolderIcon,
  Label as LabelIcon,
  FolderOpen as FolderOpenIcon,
  FolderSpecial as FolderSpecialIcon,
  StarBorder as StarBorderIcon,
  CollectionsBookmark as CollectionsBookmarkIcon,
  NoteAlt as NoteAltIcon,
  Close as CloseIcon,
  CheckCircle as SelectedIcon,
  Newspaper as NewspaperIcon,
  HowToReg,
  Article as FlyerIcon,
  Build as FixIcon,
  Policy as PolicyIcon,
  Church as ChurchIcon,
  MenuBook as BibleIcon,
  Add as AddIcon,
  Event as EventIcon,
  Today as TodayIcon,
  AccessTime as AccessTimeIcon,
  Schedule as ScheduleIcon,
  School as SchoolIcon,
  Group as GroupIcon,
  Groups as GroupsIcon,
  FamilyRestroom as FamilyRestroomIcon,
  EmojiPeople as EmojiPeopleIcon,
  Celebration as CelebrationIcon,
  Home as HomeIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Info as InfoIcon,
  HelpOutline as HelpOutlineIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Checklist as ChecklistIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  CalendarMonth as CalendarMonthIcon,
  VolunteerActivism as VolunteerActivismIcon,
  LocalLibrary as LocalLibraryIcon,
  LocalOffer as LocalOfferIcon,
  LocalFlorist as LocalFloristIcon,
  WbSunny as WbSunnyIcon,
  Nightlight as NightlightIcon,
} from '@mui/icons-material';

// Static links
import STATIC_LINKS from '../../config/SidebarLinks';

// Services
import {
  listCustomFolders,
  createCustomFolder,
  updateCustomFolder,
  deleteCustomFolder,
  getFilesMetaByPrefix,
  renameFileVisual,
  setFileCategory,
  setFileTags,
  upsertFileMeta,
  getStaticLinkOverrides,
  setStaticLinkOverrideTitle,
  getUserNotes,
  setUserNotes,
} from '../../utils/firestoreService';

import {
  uploadFileWithMetadata,
  deleteFile,
  getStorageUsage,
  makeStorageSafeName,
  listFiles,
  getDownloadURLByPath,
} from '../../utils/storageService';

import '../../styles/Sidebar.css';

import ClassStudentsModal from '../ClassStudentsModal';

const functions = getFunctions(undefined, 'europe-west1');

/* --------------------------- CONFIG ----------------------------------- */
const STORAGE_LIMIT_BYTES = 5 * 1024 * 1024 * 1024; // 5GB
const OG_IMG = '/images/og-white.jpg';
const ADMIN_EMAIL = 'diariodibordo@redentoremonserrato.it'.toLowerCase();
const OWNER_EMAIL = 'davidedessi@me.com';
const REPORTS_EXTRA_EMAIL = 'saba.nicoletta1@gmail.com';
const BADGE_LOCALSTORAGE_KEY = (uid) => `sidebar.uploads.seen.count.${uid || 'anon'}`;

/* --------------------------- HELPERS ---------------------------------- */
const isAdmin = (u) => !!(u?.email && u.email.toLowerCase() === ADMIN_EMAIL);
const isOwner = (u) => !!(u?.email && u.email.toLowerCase() === OWNER_EMAIL);

const human = (bytes) => {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${(n / Math.pow(k, i)).toFixed(i ? 1 : 0)} ${sizes[i]}`;
};

const safeDisplay = (name) =>
  (name || '').replace(/[-_]+/g, ' ').replace(/\s\s+/g, ' ').trim();

const matchQuery = (txt, q) => (txt || '').toLowerCase().includes((q || '').toLowerCase());

const sortFolders = (a, b) => {
  const ai = Number.isFinite(a?.index) ? a.index : Number.MAX_SAFE_INTEGER;
  const bi = Number.isFinite(b?.index) ? b.index : Number.MAX_SAFE_INTEGER;
  if (ai !== bi) return ai - bi;
  return (a?.label || '').localeCompare(b?.label || '', 'it', { sensitivity: 'base' });
};

const FOLDER_ICONS = [
  { key: 'folder', icon: <FolderIcon /> },
  { key: 'folderOpen', icon: <FolderOpenIcon /> },
  { key: 'folderSpecial', icon: <FolderSpecialIcon /> },
  { key: 'starBorder', icon: <StarBorderIcon /> },
  { key: 'bookmark', icon: <CollectionsBookmarkIcon /> },
];
const renderFolderIconByKey = (key) => (FOLDER_ICONS.find((x) => x.key === key)?.icon || <FolderIcon />);

/* rows monoriga con ellissi per tendine/menu */
const oneLine = {
  display: 'flex',
  alignItems: 'center',
  gap: 0,              // spazio ridotto vicino all'icona
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minHeight: 36,
  fontSize: '0.90rem',
};

/* --- ICONS per i 5 pulsanti personalizzabili --- */
const CUSTOM_BUTTON_ICONS = [
  { key: 'open', label: 'Link esterno', icon: <OpenInNew fontSize="small" /> },
  { key: 'newspaper', label: 'Giornale', icon: <NewspaperIcon fontSize="small" /> },
  { key: 'flyer', label: 'Volantino', icon: <FlyerIcon fontSize="small" /> },
  { key: 'note', label: 'Appunti', icon: <NoteAltIcon fontSize="small" /> },
  { key: 'policy', label: 'Documento', icon: <PolicyIcon fontSize="small" /> },
  { key: 'cloud', label: 'Upload', icon: <CloudUpload fontSize="small" /> },
  { key: 'folder', label: 'Cartella', icon: <FolderIcon fontSize="small" /> },
  { key: 'star', label: 'Stella', icon: <StarBorderIcon fontSize="small" /> },
  { key: 'church', label: 'Parrocchia', icon: <ChurchIcon fontSize="small" /> },
  { key: 'bible', label: 'Vangelo', icon: <BibleIcon fontSize="small" /> },
  { key: 'howToReg', label: 'Approvazioni', icon: <HowToReg fontSize="small" /> },
  { key: 'fix', label: 'Strumenti', icon: <FixIcon fontSize="small" /> },
  { key: 'newFolder', label: 'Nuova cartella', icon: <CreateFolderIcon fontSize="small" /> },
  // Icone aggiuntive per i pulsanti personalizzabili / promemoria
  { key: 'event', label: 'Evento', icon: <EventIcon fontSize="small" /> },
  { key: 'today', label: 'Oggi', icon: <TodayIcon fontSize="small" /> },
  { key: 'time', label: 'Orario', icon: <AccessTimeIcon fontSize="small" /> },
  { key: 'schedule', label: 'Calendario', icon: <ScheduleIcon fontSize="small" /> },
  { key: 'school', label: 'Scuola', icon: <SchoolIcon fontSize="small" /> },
  { key: 'group', label: 'Gruppo', icon: <GroupIcon fontSize="small" /> },
  { key: 'groups', label: 'Gruppi', icon: <GroupsIcon fontSize="small" /> },
  { key: 'family', label: 'Famiglia', icon: <FamilyRestroomIcon fontSize="small" /> },
  { key: 'people', label: 'Persone', icon: <EmojiPeopleIcon fontSize="small" /> },
  { key: 'celebration', label: 'Festa', icon: <CelebrationIcon fontSize="small" /> },
  { key: 'home', label: 'Casa', icon: <HomeIcon fontSize="small" /> },
  { key: 'favorite', label: 'Importante', icon: <FavoriteBorderIcon fontSize="small" /> },
  { key: 'info', label: 'Informazioni', icon: <InfoIcon fontSize="small" /> },
  { key: 'help', label: 'Aiuto', icon: <HelpOutlineIcon fontSize="small" /> },
  { key: 'settings', label: 'Impostazioni', icon: <SettingsIcon fontSize="small" /> },
  { key: 'assessment', label: 'Statistiche', icon: <AssessmentIcon fontSize="small" /> },
  { key: 'checklist', label: 'Elenco', icon: <ChecklistIcon fontSize="small" /> },
  { key: 'checkCircle', label: 'Confermato', icon: <CheckCircleOutlineIcon fontSize="small" /> },
  { key: 'calendarMonth', label: 'Mese', icon: <CalendarMonthIcon fontSize="small" /> },
  { key: 'volunteer', label: 'Volontariato', icon: <VolunteerActivismIcon fontSize="small" /> },
  { key: 'library', label: 'Biblioteca', icon: <LocalLibraryIcon fontSize="small" /> },
  { key: 'offer', label: 'Offerta', icon: <LocalOfferIcon fontSize="small" /> },
  { key: 'florist', label: 'Fiori', icon: <LocalFloristIcon fontSize="small" /> },
  { key: 'sun', label: 'Sole', icon: <WbSunnyIcon fontSize="small" /> },
  { key: 'night', label: 'Notte', icon: <NightlightIcon fontSize="small" /> },
];

/* ---------------------- ICON SET PROMEMORIA APPBAR ---------------------- */
/**
 * Mappa delle icone disponibili per il promemoria manuale in AppBar.
 * Le chiavi sono stringhe stabili usate da Dashboard (APPBAR_MANUAL_ICON_EMOJI[key]),
 * ogni valore contiene etichetta e emoji visuale.
 */
export const APPBAR_MANUAL_ICON_EMOJI = {
  star:           { label: 'Stella',                emoji: '⭐' },
  bell:           { label: 'Campanella',            emoji: '🔔' },
  info:           { label: 'Informazioni',          emoji: 'ℹ️' },
  warning:        { label: 'Avviso',                emoji: '⚠️' },
  check:          { label: 'Confermato',            emoji: '✅' },
  heart:          { label: 'Cuore',                 emoji: '❤️' },
  heart_blue:     { label: 'Cuore blu',             emoji: '💙' },
  heart_green:    { label: 'Cuore verde',           emoji: '💚' },
  heart_yellow:   { label: 'Cuore giallo',          emoji: '💛' },
  heart_purple:   { label: 'Cuore viola',           emoji: '💜' },
  star2:          { label: 'Stelle',                emoji: '✨' },
  sparkles:       { label: 'Brillìo',               emoji: '🌟' },
  fire:           { label: 'Fuoco',                 emoji: '🔥' },
  clap:           { label: 'Applausi',              emoji: '👏' },
  pray:           { label: 'Preghiera',             emoji: '🙏' },
  hands_up:       { label: 'Gioia',                 emoji: '🙌' },
  smile:          { label: 'Sorriso',               emoji: '😊' },
  smile_halo:     { label: 'Sorriso santo',         emoji: '😇' },
  smile_heart:    { label: 'Sorriso affettuoso',    emoji: '🥰' },
  party:          { label: 'Festa',                 emoji: '🥳' },
  tada:           { label: 'Festone',               emoji: '🎉' },
  balloon:        { label: 'Palloncino',            emoji: '🎈' },
  gift:           { label: 'Regalo',                emoji: '🎁' },

  cross:          { label: 'Croce',                 emoji: '✝️' },
  cross_ornate:   { label: 'Croce dorata',          emoji: '⛪' },
  church:         { label: 'Chiesa',                emoji: '⛪' },
  dove:           { label: 'Colomba Spirito Santo', emoji: '🕊️' },
  candle:         { label: 'Candela',               emoji: '🕯️' },
  bible:          { label: 'Bibbia',                emoji: '📖' },
  rosary:         { label: 'Rosario',               emoji: '📿' },
  chalice:        { label: 'Calice',                emoji: '🍷' },
  bread:          { label: 'Pane',                  emoji: '🍞' },
  fish:           { label: 'Pesce',                 emoji: '🐟' },
  angel:          { label: 'Angelo',                emoji: '👼' },
  shepherd:       { label: 'Pastore',               emoji: '🧑‍🌾' },
  sheep:          { label: 'Pecora',                emoji: '🐑' },
  flock:          { label: 'Gregge',                emoji: '🐑' },
  heart_cross:    { label: 'Cuore e croce',         emoji: '❤️‍🔥' },

  calendar:       { label: 'Calendario',            emoji: '📅' },
  calendar_spiral:{ label: 'Calendario spirale',    emoji: '🗓️' },
  clock:          { label: 'Orologio',              emoji: '🕒' },
  alarm:          { label: 'Sveglia',               emoji: '⏰' },
  hourglass:      { label: 'Clessidra',             emoji: '⌛' },
  stopwatch:      { label: 'Cronometro',            emoji: '⏱️' },
  sunrise:        { label: 'Alba',                  emoji: '🌅' },
  sunset:         { label: 'Tramonto',              emoji: '🌇' },
  sun:            { label: 'Sole',                  emoji: '☀️' },
  moon:           { label: 'Luna',                  emoji: '🌙' },
  stars:          { label: 'Cielo stellato',        emoji: '🌌' },

  school:         { label: 'Scuola',                emoji: '🏫' },
  backpack:       { label: 'Zaino',                 emoji: '🎒' },
  book:           { label: 'Libro',                 emoji: '📘' },
  books:          { label: 'Libri',                 emoji: '📚' },
  pencil:         { label: 'Matita',                emoji: '✏️' },
  pen:            { label: 'Penna',                 emoji: '🖊️' },
  crayons:        { label: 'Pastelli',              emoji: '🖍️' },
  ruler:          { label: 'Righello',              emoji: '📏' },
  notebook:       { label: 'Quaderno',              emoji: '📓' },
  teacher:        { label: 'Insegnante',            emoji: '👩‍🏫' },
  students:       { label: 'Studenti',              emoji: '🧑‍🎓' },
  kids:           { label: 'Bambini',               emoji: '🧒' },
  family:         { label: 'Famiglia',              emoji: '👨‍👩‍👧‍👦' },
  group:          { label: 'Gruppo',                emoji: '👥' },

  megaphone:      { label: 'Annuncio',              emoji: '📣' },
  email:          { label: 'Messaggio',             emoji: '✉️' },
  phone:          { label: 'Telefono',              emoji: '☎️' },
  meeting:        { label: 'Riunione',              emoji: '🗣️' },
  handshake:      { label: 'Accordo',               emoji: '🤝' },
  coffee:         { label: 'Caffè',                 emoji: '☕' },
  tea:            { label: 'Tisana',                emoji: '🍵' },
  lunch:          { label: 'Pranzo',                emoji: '🍽️' },
  cake:           { label: 'Torta',                 emoji: '🍰' },
  pizza:          { label: 'Pizza',                 emoji: '🍕' },

  hands_heart:    { label: 'Cuore tra le mani',     emoji: '🫶' },
  giving:         { label: 'Dare',                  emoji: '🤲' },
  donate:         { label: 'Donazione',             emoji: '💝' },
  box_food:       { label: 'Scatola viveri',        emoji: '📦' },
  shopping_cart:  { label: 'Spesa',                 emoji: '🛒' },

  leaf:           { label: 'Foglia',                emoji: '🍃' },
  four_leaves:    { label: 'Quadrifoglio',          emoji: '🍀' },
  flower:         { label: 'Fiore',                 emoji: '🌸' },
  rose:           { label: 'Rosa',                  emoji: '🌹' },
  sunflower:      { label: 'Girasole',              emoji: '🌻' },
  snowflake:      { label: 'Fiocco di neve',        emoji: '❄️' },
  snowman:        { label: 'Pupazzo di neve',       emoji: '☃️' },
  tree:           { label: 'Albero',                emoji: '🌳' },
  christmas_tree: { label: 'Albero di Natale',      emoji: '🎄' },
  seedling:       { label: 'Germoglio',             emoji: '🌱' },

  idea:           { label: 'Lampadina',             emoji: '💡' },
  target:         { label: 'Bersaglio',             emoji: '🎯' },
  pin:            { label: 'Puntina',               emoji: '📌' },
  location:       { label: 'Luogo',                 emoji: '📍' },
  map:            { label: 'Mappa',                 emoji: '🗺️' },
  lock:           { label: 'Lucchetto',             emoji: '🔒' },
  unlock:         { label: 'Lucchetto aperto',      emoji: '🔓' },
  key:            { label: 'Chiave',                emoji: '🔑' },
  star_circle:    { label: 'Stella in cerchio',     emoji: '⭕' },

  wow:            { label: 'Sorpresa',              emoji: '😮' },
  thinking:       { label: 'Pensieroso',            emoji: '🤔' },
  relief:         { label: 'Sospiro di sollievo',   emoji: '😌' },
  cool:           { label: 'Occhiali da sole',      emoji: '😎' },
  cry:            { label: 'Lacrime',               emoji: '😢' },
  cry_joy:        { label: 'Risata con lacrime',    emoji: '😂' },
  grateful:       { label: 'Grato',                 emoji: '🤗' },

  generic_001: { label: 'Emoji 1', emoji: '🔹' },
  generic_002: { label: 'Emoji 2', emoji: '🔸' },
  generic_003: { label: 'Emoji 3', emoji: '🔷' },
  generic_004: { label: 'Emoji 4', emoji: '🔶' },
  generic_005: { label: 'Emoji 5', emoji: '⬜' },
  generic_006: { label: 'Emoji 6', emoji: '⬛' },
  generic_007: { label: 'Emoji 7', emoji: '⚪' },
  generic_008: { label: 'Emoji 8', emoji: '⚫' },
  generic_009: { label: 'Emoji 9', emoji: '🟢' },
  generic_010: { label: 'Emoji 10', emoji: '🔵' },
  generic_011: { label: 'Emoji 11', emoji: '🔹' },
  generic_012: { label: 'Emoji 12', emoji: '🔸' },
  generic_013: { label: 'Emoji 13', emoji: '🔷' },
  generic_014: { label: 'Emoji 14', emoji: '🔶' },
  generic_015: { label: 'Emoji 15', emoji: '⬜' },
  generic_016: { label: 'Emoji 16', emoji: '⬛' },
  generic_017: { label: 'Emoji 17', emoji: '⚪' },
  generic_018: { label: 'Emoji 18', emoji: '⚫' },
  generic_019: { label: 'Emoji 19', emoji: '🟢' },
  generic_020: { label: 'Emoji 20', emoji: '🔵' },
  generic_021: { label: 'Emoji 21', emoji: '🔹' },
  generic_022: { label: 'Emoji 22', emoji: '🔸' },
  generic_023: { label: 'Emoji 23', emoji: '🔷' },
  generic_024: { label: 'Emoji 24', emoji: '🔶' },
  generic_025: { label: 'Emoji 25', emoji: '⬜' },
  generic_026: { label: 'Emoji 26', emoji: '⬛' },
  generic_027: { label: 'Emoji 27', emoji: '⚪' },
  generic_028: { label: 'Emoji 28', emoji: '⚫' },
  generic_029: { label: 'Emoji 29', emoji: '🟢' },
  generic_030: { label: 'Emoji 30', emoji: '🔵' },
  generic_031: { label: 'Emoji 31', emoji: '🔹' },
  generic_032: { label: 'Emoji 32', emoji: '🔸' },
  generic_033: { label: 'Emoji 33', emoji: '🔷' },
  generic_034: { label: 'Emoji 34', emoji: '🔶' },
  generic_035: { label: 'Emoji 35', emoji: '⬜' },
  generic_036: { label: 'Emoji 36', emoji: '⬛' },
  generic_037: { label: 'Emoji 37', emoji: '⚪' },
  generic_038: { label: 'Emoji 38', emoji: '⚫' },
  generic_039: { label: 'Emoji 39', emoji: '🟢' },
  generic_040: { label: 'Emoji 40', emoji: '🔵' },
  generic_041: { label: 'Emoji 41', emoji: '🔹' },
  generic_042: { label: 'Emoji 42', emoji: '🔸' },
  generic_043: { label: 'Emoji 43', emoji: '🔷' },
  generic_044: { label: 'Emoji 44', emoji: '🔶' },
  generic_045: { label: 'Emoji 45', emoji: '⬜' },
  generic_046: { label: 'Emoji 46', emoji: '⬛' },
  generic_047: { label: 'Emoji 47', emoji: '⚪' },
  generic_048: { label: 'Emoji 48', emoji: '⚫' },
  generic_049: { label: 'Emoji 49', emoji: '🟢' },
  generic_050: { label: 'Emoji 50', emoji: '🔵' },
  generic_051: { label: 'Emoji 51', emoji: '🔹' },
  generic_052: { label: 'Emoji 52', emoji: '🔸' },
  generic_053: { label: 'Emoji 53', emoji: '🔷' },
  generic_054: { label: 'Emoji 54', emoji: '🔶' },
  generic_055: { label: 'Emoji 55', emoji: '⬜' },
  generic_056: { label: 'Emoji 56', emoji: '⬛' },
  generic_057: { label: 'Emoji 57', emoji: '⚪' },
  generic_058: { label: 'Emoji 58', emoji: '⚫' },
  generic_059: { label: 'Emoji 59', emoji: '🟢' },
  generic_060: { label: 'Emoji 60', emoji: '🔵' },
  generic_061: { label: 'Emoji 61', emoji: '🔹' },
  generic_062: { label: 'Emoji 62', emoji: '🔸' },
  generic_063: { label: 'Emoji 63', emoji: '🔷' },
  generic_064: { label: 'Emoji 64', emoji: '🔶' },
  generic_065: { label: 'Emoji 65', emoji: '⬜' },
  generic_066: { label: 'Emoji 66', emoji: '⬛' },
  generic_067: { label: 'Emoji 67', emoji: '⚪' },
  generic_068: { label: 'Emoji 68', emoji: '⚫' },
  generic_069: { label: 'Emoji 69', emoji: '🟢' },
  generic_070: { label: 'Emoji 70', emoji: '🔵' },
  generic_071: { label: 'Emoji 71', emoji: '🔹' },
  generic_072: { label: 'Emoji 72', emoji: '🔸' },
  generic_073: { label: 'Emoji 73', emoji: '🔷' },
  generic_074: { label: 'Emoji 74', emoji: '🔶' },
  generic_075: { label: 'Emoji 75', emoji: '⬜' },
  generic_076: { label: 'Emoji 76', emoji: '⬛' },
  generic_077: { label: 'Emoji 77', emoji: '⚪' },
  generic_078: { label: 'Emoji 78', emoji: '⚫' },
  generic_079: { label: 'Emoji 79', emoji: '🟢' },
  generic_080: { label: 'Emoji 80', emoji: '🔵' },
  generic_081: { label: 'Emoji 81', emoji: '🔹' },
  generic_082: { label: 'Emoji 82', emoji: '🔸' },
  generic_083: { label: 'Emoji 83', emoji: '🔷' },
  generic_084: { label: 'Emoji 84', emoji: '🔶' },
  generic_085: { label: 'Emoji 85', emoji: '⬜' },
  generic_086: { label: 'Emoji 86', emoji: '⬛' },
  generic_087: { label: 'Emoji 87', emoji: '⚪' },
  generic_088: { label: 'Emoji 88', emoji: '⚫' },
  generic_089: { label: 'Emoji 89', emoji: '🟢' },
  generic_090: { label: 'Emoji 90', emoji: '🔵' },
  generic_091: { label: 'Emoji 91', emoji: '🔹' },
  generic_092: { label: 'Emoji 92', emoji: '🔸' },
  generic_093: { label: 'Emoji 93', emoji: '🔷' },
  generic_094: { label: 'Emoji 94', emoji: '🔶' },
  generic_095: { label: 'Emoji 95', emoji: '⬜' },
  generic_096: { label: 'Emoji 96', emoji: '⬛' },
  generic_097: { label: 'Emoji 97', emoji: '⚪' },
  generic_098: { label: 'Emoji 98', emoji: '⚫' },
  generic_099: { label: 'Emoji 99', emoji: '🟢' },
  generic_100: { label: 'Emoji 100', emoji: '🔵' },
  generic_101: { label: 'Emoji 101', emoji: '🔹' },
  generic_102: { label: 'Emoji 102', emoji: '🔸' },
  generic_103: { label: 'Emoji 103', emoji: '🔷' },
  generic_104: { label: 'Emoji 104', emoji: '🔶' },
  generic_105: { label: 'Emoji 105', emoji: '⬜' },
  generic_106: { label: 'Emoji 106', emoji: '⬛' },
  generic_107: { label: 'Emoji 107', emoji: '⚪' },
  generic_108: { label: 'Emoji 108', emoji: '⚫' },
  generic_109: { label: 'Emoji 109', emoji: '🟢' },
  generic_110: { label: 'Emoji 110', emoji: '🔵' },
  generic_111: { label: 'Emoji 111', emoji: '🔹' },
  generic_112: { label: 'Emoji 112', emoji: '🔸' },
  generic_113: { label: 'Emoji 113', emoji: '🔷' },
  generic_114: { label: 'Emoji 114', emoji: '🔶' },
  generic_115: { label: 'Emoji 115', emoji: '⬜' },
  generic_116: { label: 'Emoji 116', emoji: '⬛' },
  generic_117: { label: 'Emoji 117', emoji: '⚪' },
  generic_118: { label: 'Emoji 118', emoji: '⚫' },
  generic_119: { label: 'Emoji 119', emoji: '🟢' },
  generic_120: { label: 'Emoji 120', emoji: '🔵' },
  generic_121: { label: 'Emoji 121', emoji: '🔹' },
  generic_122: { label: 'Emoji 122', emoji: '🔸' },
  generic_123: { label: 'Emoji 123', emoji: '🔷' },
  generic_124: { label: 'Emoji 124', emoji: '🔶' },
  generic_125: { label: 'Emoji 125', emoji: '⬜' },
  generic_126: { label: 'Emoji 126', emoji: '⬛' },
  generic_127: { label: 'Emoji 127', emoji: '⚪' },
  generic_128: { label: 'Emoji 128', emoji: '⚫' },
  generic_129: { label: 'Emoji 129', emoji: '🟢' },
  generic_130: { label: 'Emoji 130', emoji: '🔵' },
  generic_131: { label: 'Emoji 131', emoji: '🔹' },
  generic_132: { label: 'Emoji 132', emoji: '🔸' },
  generic_133: { label: 'Emoji 133', emoji: '🔷' },
  generic_134: { label: 'Emoji 134', emoji: '🔶' },
  generic_135: { label: 'Emoji 135', emoji: '⬜' },
  generic_136: { label: 'Emoji 136', emoji: '⬛' },
  generic_137: { label: 'Emoji 137', emoji: '⚪' },
  generic_138: { label: 'Emoji 138', emoji: '⚫' },
  generic_139: { label: 'Emoji 139', emoji: '🟢' },
  generic_140: { label: 'Emoji 140', emoji: '🔵' },
  generic_141: { label: 'Emoji 141', emoji: '🔹' },
  generic_142: { label: 'Emoji 142', emoji: '🔸' },
  generic_143: { label: 'Emoji 143', emoji: '🔷' },
  generic_144: { label: 'Emoji 144', emoji: '🔶' },
  generic_145: { label: 'Emoji 145', emoji: '⬜' },
  generic_146: { label: 'Emoji 146', emoji: '⬛' },
  generic_147: { label: 'Emoji 147', emoji: '⚪' },
  generic_148: { label: 'Emoji 148', emoji: '⚫' },
  generic_149: { label: 'Emoji 149', emoji: '🟢' },
  generic_150: { label: 'Emoji 150', emoji: '🔵' },
  generic_151: { label: 'Emoji 151', emoji: '🔹' },
  generic_152: { label: 'Emoji 152', emoji: '🔸' },
  generic_153: { label: 'Emoji 153', emoji: '🔷' },
  generic_154: { label: 'Emoji 154', emoji: '🔶' },
  generic_155: { label: 'Emoji 155', emoji: '⬜' },
  generic_156: { label: 'Emoji 156', emoji: '⬛' },
  generic_157: { label: 'Emoji 157', emoji: '⚪' },
  generic_158: { label: 'Emoji 158', emoji: '⚫' },
  generic_159: { label: 'Emoji 159', emoji: '🟢' },
  generic_160: { label: 'Emoji 160', emoji: '🔵' },
};


const APPBAR_ICON_GROUPS = [
  {
    id: 'core',
    label: 'Preferiti',
    keys: [
      'star','star2','sparkles','heart','heart_blue','heart_green','heart_yellow','heart_purple',
      'tada','party','clap','pray','smile','smile_halo','smile_heart','hands_up'
    ],
  },
  {
    id: 'liturgia',
    label: 'Liturgia & Parrocchia',
    keys: [
      'cross','cross_ornate','church','dove','candle','bible','rosary','chalice','bread','fish',
      'angel','shepherd','sheep','flock','heart_cross'
    ],
  },
  {
    id: 'tempo',
    label: 'Tempo & Calendario',
    keys: [
      'calendar','calendar_spiral','clock','alarm','hourglass','stopwatch','sunrise','sunset','sun','moon','stars'
    ],
  },
  {
    id: 'scuola',
    label: 'Scuola & Ragazzi',
    keys: [
      'school','backpack','book','books','pencil','pen','crayons','ruler','notebook','teacher','students','kids','family','group'
    ],
  },
  {
    id: 'eventi',
    label: 'Eventi & Incontri',
    keys: [
      'megaphone','email','phone','meeting','handshake','coffee','tea','lunch','cake','pizza'
    ],
  },
  {
    id: 'carita',
    label: 'Carità & Volontariato',
    keys: [
      'hands_heart','giving','donate','box_food','shopping_cart'
    ],
  },
  {
    id: 'stagioni',
    label: 'Stagioni & Natura',
    keys: [
      'leaf','four_leaves','flower','rose','sunflower','snowflake','snowman','tree','christmas_tree','seedling'
    ],
  },
  {
    id: 'simboli',
    label: 'Simboli',
    keys: [
      'idea','target','pin','location','map','lock','unlock','key','star_circle'
    ],
  },
  {
    id: 'emoticon',
    label: 'Emoticon',
    keys: [
      'wow','thinking','relief','cool','cry','cry_joy','grateful'
    ],
  },
  {
    id: 'extra',
    label: 'Extra (varie)',
    keys: [
      'generic_001','generic_002','generic_003','generic_004','generic_005',
      'generic_006','generic_007','generic_008','generic_009','generic_010',
      'generic_011','generic_012','generic_013','generic_014','generic_015'
    ],
  },
];

// Garantisce che tutte le chiavi definite in APPBAR_MANUAL_ICON_EMOJI
// siano effettivamente selezionabili almeno in un gruppo.
// Le icone non mappate esplicitamente vengono aggiunte al gruppo "extra".
const ALL_APPBAR_MANUAL_ICON_KEYS = Object.keys(APPBAR_MANUAL_ICON_EMOJI || {});
if (ALL_APPBAR_MANUAL_ICON_KEYS.length > 0) {
  const usedAppbarManualKeys = new Set(
    APPBAR_ICON_GROUPS.flatMap((group) => group.keys || [])
  );
  const missingAppbarManualKeys = ALL_APPBAR_MANUAL_ICON_KEYS.filter(
    (key) => !usedAppbarManualKeys.has(key)
  );

  if (missingAppbarManualKeys.length > 0) {
    const extraGroup = APPBAR_ICON_GROUPS.find((g) => g.id === 'extra');
    if (extraGroup) {
      extraGroup.keys = [...extraGroup.keys, ...missingAppbarManualKeys];
    } else {
      APPBAR_ICON_GROUPS.push({
        id: 'extra_auto',
        label: 'Extra (auto)',
        keys: missingAppbarManualKeys,
      });
    }
  }
}


const AppbarIconPicker = ({ value, onChange }) => {
  if (!onChange) return null;
  return (
    <Box sx={{ maxHeight: 260, overflowY: 'auto', mb: 1.5 }}>
      {APPBAR_ICON_GROUPS.map((group) => (
        <Box key={group.id} sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ mb: 0.5, display: 'block', opacity: 0.8 }}>
            {group.label}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
              gap: 0.5,
            }}
          >
            {group.keys
              .filter((k) => !!APPBAR_MANUAL_ICON_EMOJI[k])
              .map((k) => {
                const cfg = APPBAR_MANUAL_ICON_EMOJI[k];
                const selected = value === k;
                return (
                  <Tooltip key={k} title={cfg.label}>
                    <Box
                      onClick={() => onChange(k)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        fontSize: 20,
                        lineHeight: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 0.5,
                        userSelect: 'none',
                        backgroundColor: selected ? 'action.selected' : 'background.paper',
                      }}
                    >
                      {cfg.emoji}
                    </Box>
                  </Tooltip>
                );
              })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};


const renderCustomButtonIcon = (key) =>
  (CUSTOM_BUTTON_ICONS.find((i) => i.key === key)?.icon || <OpenInNew fontSize="small" />);

const CUSTOM_BUTTON_COUNT = 5;

const makeDefaultCustomButtons = () =>
  Array.from({ length: CUSTOM_BUTTON_COUNT }, (_, idx) => ({
    id: idx,
    label: `Link ${idx + 1}`,
    url: '',
    iconKey: 'open',
    placement: 'both',
    visibility: 'all',
    visibilityEmails: '',
    menuId: 'Personalizzati',
  }));

const HEADER_BUTTON_ORDER = [
  'approvals',
  'orphans',
  'notes',
  'newFolder',
  'classStudents',
  'upload',
  'foglietto',
  'turni',
  'privacyReset',
  'privacyEditor',
  'backup',
  'appbarHighlight',
  'reports',
];

/* Default per i pulsanti header */
const HEADER_BUTTON_DEFAULTS = {
  approvals: {
    label: 'Approva iscrizioni',
    iconKey: 'howToReg',
    placement: 'both',
    visibility: 'admin',
    visibilityEmails: '',
  },
  orphans: {
    label: 'File da sistemare',
    iconKey: 'fix',
    placement: 'both',
    visibility: 'owner',
    visibilityEmails: '',
  },
  notes: {
    label: 'I miei appunti',
    iconKey: 'note',
    placement: 'both',
    visibility: 'all',
    visibilityEmails: '',
  },
  newFolder: {
    label: 'Nuova cartella',
    iconKey: 'newFolder',
    placement: 'both',
    visibility: 'all',
    visibilityEmails: '',
  },

  classStudents: {
    label: 'Classi & Studenti',
    iconKey: 'folder',
    placement: 'both',
    visibility: 'admin',
    visibilityEmails: 'davidedessi@me.com',
  },
  upload: {
    label: 'Carica file',
    iconKey: 'cloud',
    placement: 'both',
    visibility: 'all',
    visibilityEmails: '',
  },
  foglietto: {
    label: 'Foglietto parrocchiale',
    iconKey: 'newspaper',
    placement: 'both',
    visibility: 'all',
    visibilityEmails: '',
    url: 'https://www.redentoremonserrato.it/foglietto.html',
  },
  privacyReset: {
    label: 'Reset privacy utente',
    iconKey: 'fix',
    placement: 'both',
    visibility: 'admin',
    visibilityEmails: '',
  },
  turni: {
    label: 'Turni',
    iconKey: 'newspaper',
    placement: 'both',
    visibility: 'all',
    visibilityEmails: '',
  },
  privacyEditor: {
    label: 'Editor Privacy',
    iconKey: 'policy',
    placement: 'both',
    visibility: 'admin',
    visibilityEmails: '',
  },
  backup: {
    label: 'Backup & Ripristino',
    iconKey: 'cloud',
    placement: 'both',
    visibility: 'admin',
    visibilityEmails: '',
  },

  appbarHighlight: {
    label: 'Messaggio Dashboard',
    iconKey: 'star',
    placement: 'both',
    visibility: 'admin',
    visibilityEmails: '',
  },
  reports: {
    label: 'Reports',
    iconKey: 'newspaper',
    placement: 'both',
    visibility: 'admin',
    visibilityEmails: '',
  },
};

const getHeaderButtonConfig = (config, id) => {
  const base = HEADER_BUTTON_DEFAULTS[id] || {
    label: id,
    iconKey: 'open',
    placement: 'both',
    visibility: 'all',
    visibilityEmails: '',
  };
  const cfg = (config && config[id]) || {};
  return {
    ...base,
    ...cfg,
    placement: cfg.placement || base.placement || 'both',
    visibility: cfg.visibility || base.visibility || 'all',
    visibilityEmails:
      typeof cfg.visibilityEmails === 'string'
        ? cfg.visibilityEmails
        : typeof base.visibilityEmails === 'string'
        ? base.visibilityEmails
        : '',
  };
};

const canSeeByVisibility = (visibility, user, ownerView, emails) => {
  const email = user?.email ? user.email.toLowerCase() : '';
  const list = String(emails || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);

  let base = true;
  if (visibility === 'owner') base = ownerView;
  else if (visibility === 'admin') base = ownerView || isAdmin(user);

  if (!list.length) {
    return base;
  }

  const emailAllowed = !!email && list.includes(email);

  if (visibility === 'all') {
    // Se è impostata una lista di email e la visibilità è "Tutti",
    // il pulsante è visibile solo alle email elencate.
    return emailAllowed;
  }

  // Per visibilità "admin" o "owner" (o altre),
  // il pulsante è visibile se:
  // - l'utente rientra nella regola di base (admin/owner), oppure
  // - la sua email è esplicitamente elencata.
  return base || emailAllowed;
};


// Anteprima frasi automatiche per il promemoria AppBar
// Restituisce una lista di oggetti { dateLabel, text } usata solo a scopo di preview.

function getLiturgicalAutoPreview() {
  const now = new Date();
  const currentYear = now.getFullYear();

  const formatDate = (d) =>
    d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  const makeDateFromISO = (iso) => {
    const [y, m, d] = iso.split('-').map((v) => parseInt(v, 10));
    return new Date(y, m - 1, d);
  };

  const todayMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0
  );

  /**
   * Data‑driven anteprima per gli anni specifici 2025 e 2026,
   * ricavate dal Calendario Liturgico ufficiale (CEI / GCatholic).
   * Le altre annate usano una logica generica di fallback.
   */
  const previewByYear = {
    2025: [
      // Dal 19/11/2025 in poi (come richiesto)
      { date: '2025-11-23', text: "Nostro Signore Gesù Cristo Re dell'Universo (anno C)." },
      { date: '2025-11-30', text: 'I Domenica di Avvento (anno A).' },
      { date: '2025-12-07', text: 'II Domenica di Avvento (anno A).' },
      { date: '2025-12-14', text: 'III Domenica di Avvento (anno A).' },
      { date: '2025-12-21', text: 'IV Domenica di Avvento (anno A).' },
      { date: '2025-12-25', text: 'Natale del Signore.' },
      { date: '2025-12-28', text: 'Santa Famiglia di Gesù, Maria e Giuseppe (anno A).' },
    ],
    2026: [
      // Tempo di Natale 2026
      { date: '2026-01-01', text: 'Maria Santissima Madre di Dio (Solennità).' },
      { date: '2026-01-04', text: 'II Domenica di Natale.' },
      { date: '2026-01-06', text: 'Epifania del Signore (Solennità).' },
      { date: '2026-01-11', text: 'Battesimo del Signore.' },

      // Quaresima 2026 (General C – Italia)
      { date: '2026-02-18', text: 'Mercoledì delle Ceneri.' },
      { date: '2026-02-22', text: 'I Domenica di Quaresima.' },
      { date: '2026-03-01', text: 'II Domenica di Quaresima.' },
      { date: '2026-03-08', text: 'III Domenica di Quaresima.' },
      { date: '2026-03-15', text: 'IV Domenica di Quaresima «Laetare».' },
      { date: '2026-03-22', text: 'V Domenica di Quaresima.' },
      { date: '2026-03-29', text: 'Domenica delle Palme e della Passione del Signore.' },

      // Triduo Pasquale e Tempo di Pasqua 2026
      { date: '2026-04-02', text: 'Giovedì Santo – Cena del Signore.' },
      { date: '2026-04-03', text: 'Venerdì Santo – Passione del Signore.' },
      { date: '2026-04-05', text: 'Domenica di Pasqua – Risurrezione del Signore.' },
      { date: '2026-04-12', text: 'II Domenica di Pasqua (o della Divina Misericordia).' },
      { date: '2026-04-19', text: 'III Domenica di Pasqua.' },
      { date: '2026-04-26', text: 'IV Domenica di Pasqua.' },
      { date: '2026-05-03', text: 'V Domenica di Pasqua.' },
      { date: '2026-05-10', text: 'VI Domenica di Pasqua.' },
      { date: '2026-05-14', text: 'Ascensione del Signore.' },
      { date: '2026-05-17', text: 'VII Domenica di Pasqua.' },
      { date: '2026-05-24', text: 'Domenica di Pentecoste.' },

      // Solennità successive significative (Corpus Domini ecc.)
      { date: '2026-05-31', text: 'Santissima Trinità.' },
      { date: '2026-06-07', text: 'Santissimo Corpo e Sangue di Cristo (Corpus Domini).' },
      { date: '2026-06-12', text: 'Sacratissimo Cuore di Gesù.' },
    ],
  };

  const rawYearItems = previewByYear[currentYear] || [];

  const items = rawYearItems
    .map((item) => {
      const date = makeDateFromISO(item.date);
      return { date, text: item.text };
    })
    // filtra gli eventi già trascorsi
    .filter((item) => item.date.getTime() >= todayMidnight.getTime());

  // Se siamo fuori dall'intervallo (anno diverso o nessun evento futuro),
  // ricadiamo su una logica generica simile a quella precedente.
  if (items.length === 0) {
    const genericItems = [];

    const makeItem = (date, text) => ({
      date,
      text,
    });

    // Avvento (approssimato come 4 domeniche prima del 25 dicembre)
    const christmas = new Date(currentYear, 11, 25);
    const adventStart = new Date(christmas);
    adventStart.setDate(adventStart.getDate() - 28);
    if (todayMidnight <= christmas) {
      genericItems.push(
        makeItem(
          adventStart,
          'Tempo di Avvento: prepariamo il cuore ad accogliere Gesù.'
        ),
        makeItem(
          new Date(
            adventStart.getFullYear(),
            adventStart.getMonth(),
            adventStart.getDate() + 7
          ),
          'Seconda domenica di Avvento: vegliamo nella speranza.'
        )
      );
    }

    // Natale (25 dicembre)
    const nativity = new Date(currentYear, 11, 25);
    genericItems.push(
      makeItem(
        nativity,
        'Natale del Signore: oggi è nato per noi il Salvatore.'
      )
    );

    // Maria Madre di Dio (1 gennaio)
    const janFirst = new Date(
      currentYear + (todayMidnight.getMonth() === 11 ? 1 : 0),
      0,
      1
    );
    genericItems.push(
      makeItem(
        janFirst,
        'Maria Madre di Dio: affidiamo a lei il nuovo anno pastorale.'
      )
    );

    // Epifania (6 gennaio)
    const epiphany = new Date(janFirst.getFullYear(), 0, 6);
    genericItems.push(
      makeItem(
        epiphany,
        'Epifania del Signore: camminiamo nella luce di Cristo.'
      )
    );

    // Manteniamo solo date non passate
    genericItems.forEach((g) => {
      if (g.date.getTime() >= todayMidnight.getTime()) {
        items.push(g);
      }
    });
  }

  // Rimuovi eventuali duplicati (stessa data + testo) e ordina per data
  const unique = [];
  const seen = new Set();
  items.forEach((it) => {
    const key = `${it.date.getTime()}-${it.text}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(it);
    }
  });

  unique.sort((a, b) => a.date.getTime() - b.date.getTime());

  return unique.map((it) => ({
    dateLabel: formatDate(it.date),
    text: it.text,
  }));
}
/* --------------------------- COMPONENT -------------------------------- */
export default function Sidebar({ isOpen = false, onClose, toggleSidebar }) {
  const navigate = useNavigate();
  const handleClose = useCallback(() => {
    if (typeof onClose === 'function') return onClose();
    if (typeof toggleSidebar === 'function') return toggleSidebar();
  }, [onClose, toggleSidebar]);

  // close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && isOpen) handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  // Auth
  const [user, setUser] = useState(null);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub && unsub();
  }, []);

  // UI state
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState({ open: false, type: 'success', msg: '' });

  // Anchor per menu pulsanti (Admin, Link, Personalizzati)
  const [adminMenuAnchor, setAdminMenuAnchor] = useState(null);
  const [linksMenuAnchor, setLinksMenuAnchor] = useState(null);
  const [customMenuAnchor, setCustomMenuAnchor] = useState(null);

  // Configurazione pulsanti header (Admin / Link / vari)
  const [headerButtonsConfig, setHeaderButtonsConfig] = useState(HEADER_BUTTON_DEFAULTS);
  const [headerButtonsConfigDialogOpen, setHeaderButtonsConfigDialogOpen] = useState(false);
  const [headerButtonsConfigDraft, setHeaderButtonsConfigDraft] = useState(HEADER_BUTTON_DEFAULTS);
  const [selectedHeaderButtonId, setSelectedHeaderButtonId] = useState(HEADER_BUTTON_ORDER[0]);

  // Usage
  const [usage, setUsage] = useState({ usedBytes: 0 });
  const usedPct = useMemo(() => {
    const usedBytes = Number(usage?.usedBytes || 0);
    const pct = (usedBytes / STORAGE_LIMIT_BYTES) * 100;
    return Math.max(0, Math.min(100, Math.round(pct || 0)));
  }, [usage]);

  // Data
  const [customFolders, setCustomFolders] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [, setAllTags] = useState([]); // solo setter usato per raccogliere tag
  const [staticOverrides, setStaticOverrides] = useState({});

  // Notes
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [notesBusy, setNotesBusy] = useState(false);

  // Context menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTarget, setMenuTarget] = useState(null);

  // Dialogs
  const [dlgEditItemOpen, setDlgEditItemOpen] = useState(false);
  const [dlgEditItemData, setDlgEditItemData] = useState({
    type: null, id: null, label: '', visibility: 'public', raw: null,
    moveTo: null, iconKey: 'folder',
  });

  const [dlgMoveOpen, setDlgMoveOpen] = useState(false);
  const [dlgMoveTargetFile, setDlgMoveTargetFile] = useState(null);
  const [moveFilter, setMoveFilter] = useState('');

  const [dlgTagsOpen, setDlgTagsOpen] = useState(false);
  const [dlgTagsValue, setDlgTagsValue] = useState('');

  const [dlgNewFolderOpen, setDlgNewFolderOpen] = useState(false);
  const [classStudentsOpen, setClassStudentsOpen] = useState(false);
  const [dlgNewFolderData, setDlgNewFolderData] = useState({
    label: '',
    iconKey: 'folder',
    visibility: 'public',
  });

  const [multiMode, setMultiMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Editor Privacy
  const [privacyEditorOpen, setPrivacyEditorOpen] = useState(false);
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [privacyText, setPrivacyText] = useState('');
  const [privacyVersion, setPrivacyVersion] = useState('');
  const [privacyResetDialogOpen, setPrivacyResetDialogOpen] = useState(false);
  const [privacyResetEmail, setPrivacyResetEmail] = useState('');
  const [privacyResetBusy, setPrivacyResetBusy] = useState(false);

  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backups, setBackups] = useState([]);
  const refreshBackups = useCallback(async () => {
    try {
      if (!user) {
        setBackups([]);
        return;
      }
      const colRef = collection(db, 'backups');
      const snap = await getDocs(colRef);
      const items = [];
      snap.forEach((docSnap) => {
        items.push({ id: docSnap.id, ...docSnap.data() });
      });
      items.sort((a, b) => {
        const ta = a?.createdAt && a.createdAt.toMillis ? a.createdAt.toMillis() : 0;
        const tb = b?.createdAt && b.createdAt.toMillis ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      setBackups(items);
    } catch (err) {
      console.error('refreshBackups error', err);
      setSnack({ open: true, type: 'error', msg: 'Errore nel recupero dei backup.' });
    }
  }, [user]);


  const [newUploadsBadge, setNewUploadsBadge] = useState(0);
  const seenKey = useMemo(() => BADGE_LOCALSTORAGE_KEY(user?.uid), [user]);
  // Pulsanti personalizzabili (5)
  const [customButtons, setCustomButtons] = useState(makeDefaultCustomButtons);
  const [customBtnDialogOpen, setCustomBtnDialogOpen] = useState(false);
  const [editingCustomIndex, setEditingCustomIndex] = useState(null);
  const [customBtnForm, setCustomBtnForm] = useState({
    label: '',
    url: '',
    iconKey: 'open',
    placement: 'both',
    visibility: 'all',
    visibilityEmails: '',
    menuId: 'Personalizzati',
  });

  // Approvals (OWNER)
  const ownerView = isOwner(user);
  const canEditPrivacy = ownerView || isAdmin(user);

  // Email corrente in minuscolo (una sola volta)
  const userEmailLc = (user?.email || '').toLowerCase();

  // Config avanzata per pulsanti header delegabili via email
  const approvalsCfg = getHeaderButtonConfig(headerButtonsConfig, 'approvals');
  const approvalsEmailList = String(approvalsCfg.visibilityEmails || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  const approvalsEmailAllowed = !!userEmailLc && approvalsEmailList.includes(userEmailLc);
  const canUseApprovals = approvalsEmailAllowed || ownerView;

  const privacyResetCfg = getHeaderButtonConfig(headerButtonsConfig, 'privacyReset');
  const privacyResetEmailList = String(privacyResetCfg.visibilityEmails || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  const privacyResetEmailAllowed = !!userEmailLc && privacyResetEmailList.includes(userEmailLc);
  const canUsePrivacyReset = privacyResetEmailAllowed || canEditPrivacy;

  const privacyEditorCfg = getHeaderButtonConfig(headerButtonsConfig, 'privacyEditor');
  const privacyEditorEmailList = String(privacyEditorCfg.visibilityEmails || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  const privacyEditorEmailAllowed = !!userEmailLc && privacyEditorEmailList.includes(userEmailLc);
  const canUsePrivacyEditor = privacyEditorEmailAllowed || canEditPrivacy;

  const reportsCfg = getHeaderButtonConfig(headerButtonsConfig, 'reports');
  const reportsEmailList = String(reportsCfg.visibilityEmails || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  const reportsEmailAllowed = !!userEmailLc && reportsEmailList.includes(userEmailLc);
  const reportsBaseAllowed =
    !!userEmailLc && (userEmailLc === OWNER_EMAIL || userEmailLc === REPORTS_EXTRA_EMAIL);
  const canUseReports = reportsEmailAllowed || reportsBaseAllowed;

  const canEditAppbarHighlight = useMemo(
    () => !!user && (ownerView || isAdmin(user)),
    [user, ownerView]
  );

  const [dlgApprovalsOpen, setDlgApprovalsOpen] = useState(false);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  // Upload Modal State
  const [dlgUploadOpen, setDlgUploadOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState('existing'); // 'existing' | 'new'
  const [uploadSelectedFolder, setUploadSelectedFolder] = useState(''); // folderId | "static:<id>"
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNewFolderName, setUploadNewFolderName] = useState('');
  const [uploadNewFolderIconKey, setUploadNewFolderIconKey] = useState('folder');
  const [uploadNewFolderVisibility, setUploadNewFolderVisibility] = useState('public');

  const [showOrphans, setShowOrphans] = useState(false); // OWNER: sezione "Da sistemare"

  const [dlgAppbarHighlightOpen, setDlgAppbarHighlightOpen] = useState(false);
  const [appbarHighlightMode, setAppbarHighlightMode] = useState('auto'); // 'auto' | 'manual' | 'off'
  const [appbarHighlightManualText, setAppbarHighlightManualText] = useState('');

  const liturgicalPreview = useMemo(() => getLiturgicalAutoPreview(), []);

  const [appbarHighlightManualIconKey, setAppbarHighlightManualIconKey] = useState('star');

  const privacyTextareaRef = useRef(null);
  const uploadInputRef = useRef(null);

  // Helpers per editor Privacy
  const wrapSelection = (prefix, suffix = prefix) => {
    const el = privacyTextareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const value = privacyText;
    const before = value.slice(0, start);
    const sel = value.slice(start, end);
    const after = value.slice(end);
    const next = before + prefix + sel + suffix + after;
    setPrivacyText(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + prefix.length, end + prefix.length);
    }, 0);
  };

  const openPrivacyEditor = async () => {
    if (!canUsePrivacyEditor) return;
    setPrivacyEditorOpen(true);
    setPrivacyLoading(true);
    try {
      const snap = await getDoc(doc(db, 'settings', 'privacyPolicy'));
      if (snap.exists()) {
        const data = snap.data() || {};
        setPrivacyText(data.text || '');
        setPrivacyVersion(data.version || '');
      } else {
        setPrivacyText('');
        setPrivacyVersion('');
      }
    } catch (e) {
      console.error('openPrivacyEditor', e);
      setSnack({ open: true, type: 'error', msg: 'Errore nel caricamento della privacy' });
    } finally {
      setPrivacyLoading(false);
    }
  };

  const handleSavePrivacy = async () => {
    if (!canUsePrivacyEditor) return;
    setPrivacyLoading(true);
    try {
      await setDoc(
        doc(db, 'settings', 'privacyPolicy'),
        {
          text: privacyText,
          version: privacyVersion || '',
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || '',
        },
        { merge: true }
      );
      setSnack({ open: true, type: 'success', msg: 'Informativa privacy salvata' });
      setPrivacyEditorOpen(false);
    } catch (e) {
      console.error('handleSavePrivacy', e);
      setSnack({ open: true, type: 'error', msg: 'Errore nel salvataggio della privacy' });
    } finally {
      setPrivacyLoading(false);
    }
  };

  const openPrivacyResetDialog = () => {
    if (!canUsePrivacyReset) return;
    setPrivacyResetEmail('');
    setPrivacyResetDialogOpen(true);
  };

  const handleResetPrivacyForEmail = async () => {
    if (!canUsePrivacyReset) return;
    const email = (privacyResetEmail || '').trim().toLowerCase();
    if (!email) {
      setSnack({ open: true, type: 'error', msg: 'Inserisci un indirizzo email.' });
      return;
    }
    setPrivacyResetBusy(true);
    try {
      const col = collection(db, 'users');
      const qRef = query(col, where('email', '==', email));
      const snap = await getDocs(qRef);
      if (snap.empty) {
        setSnack({ open: true, type: 'error', msg: 'Nessun utente trovato per questa email.' });
        setPrivacyResetBusy(false);
        return;
      }

      const updates = [];
      snap.forEach((d) => {
        updates.push(
          updateDoc(doc(db, 'users', d.id), {
            privacyAccepted: false,
            privacyAcceptedAt: null,
            privacyVersionAccepted: null,
          })
        );
      });

      await Promise.all(updates);
      setSnack({ open: true, type: 'success', msg: 'Privacy resettata. L’utente vedrà nuovamente il consenso.' });
      setPrivacyResetDialogOpen(false);
    } catch (e) {
      console.error('handleResetPrivacyForEmail', e);
      setSnack({ open: true, type: 'error', msg: 'Errore durante il reset della privacy.' });
    } finally {
      setPrivacyResetBusy(false);
    }
  };

  /* -------------------- LOADERS -------------------- */
  const refreshUsage = useCallback(async () => {
    try {
      const u = await getStorageUsage();
      setUsage(typeof u === 'number' ? { usedBytes: u } : { usedBytes: Number(u?.usedBytes || 0) });
    } catch {
      setUsage({ usedBytes: 0 });
    }
  }, []);

  useEffect(() => {
    refreshUsage();
    const t = setInterval(refreshUsage, 30000);
    return () => clearInterval(t);
  }, [refreshUsage]);

  const refreshFolders = useCallback(async () => {
    try {
      const rows = await listCustomFolders();
      setCustomFolders(Array.isArray(rows) ? rows : []);
    } catch {
      setCustomFolders([]);
    }
  }, []);

  const mergeFilesWithMeta = (files, metas) => {
    const metaByPath = {};
    (Array.isArray(metas) ? metas : []).forEach((m) => {
      const key = m.storagePath || m.fullPath || m.path || m.name;
      if (key) metaByPath[key] = m;
    });

    const out = (Array.isArray(files) ? files : []).map((f, idx) => {
      const key = f.fullPath || f.path || `uploads/${f.name}`;
      const m = metaByPath[key] || {};
      const docId = m.id || encodeURIComponent(key);
      return {
        id: docId,
        storagePath: key,
        displayName: f.name || m.displayName || m.visualName || 'Documento',
        visualName: m.visualName || m.displayName || f.name || 'Documento',
        downloadURL: f.downloadURL,
        size: f.size || m.size || 0,
        tags: Array.isArray(m.tags) ? m.tags : [],
        categoryId: m.categoryId || null,
        index: Number.isFinite(m.index) ? m.index : idx,
      };
    });

    const tagSet = new Set();
    out.forEach((u) => (u.tags || []).forEach((t) => tagSet.add(t)));
    setAllTags(Array.from(tagSet).sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' })));

    return out;
  };

  const refreshUploads = useCallback(async () => {
    try {
      const files = await listFiles('uploads/');
      const metas = await getFilesMetaByPrefix('uploads/');
      const merged = mergeFilesWithMeta(files, metas);
      setUploads(merged);

      const seenCount = Number(localStorage.getItem(seenKey) || 0);
      const currentCount = merged.length;
      const delta = Math.max(0, currentCount - seenCount);
      setNewUploadsBadge(delta);

      setSelectedIds((prev) => {
        const next = new Set();
        merged.forEach((u) => prev.has(u.id) && next.add(u.id));
        return next;
      });
    } catch {
      setUploads([]);
    }
  }, [seenKey]);

  const refreshStaticOverrides = useCallback(async () => {
    try {
      const map = await getStaticLinkOverrides();
      setStaticOverrides(map || {});
    } catch {
      setStaticOverrides({});
    }
  }, []);

  const loadMyNotes = useCallback(async () => {
    if (!user) return;
    try {
      const n = await getUserNotes(user.uid);
      setNotesText(n?.text || '');
    } catch {
      setNotesText('');
    }
  }, [user]);

  useEffect(() => {
    refreshFolders();
    refreshUploads();
    refreshStaticOverrides();
  }, [refreshFolders, refreshUploads, refreshStaticOverrides]);

  useEffect(() => {
    loadMyNotes();
  }, [loadMyNotes]);

  // Carica config pulsanti personalizzati
  useEffect(() => {
    const loadCustomButtons = async () => {
      try {
        const ref = doc(db, 'settings', 'sidebarCustomButtons');
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() || {};
          const buttons = Array.isArray(data.buttons) ? data.buttons : [];
          const defaults = makeDefaultCustomButtons();
          const baseLength = Math.max(buttons.length, defaults.length);
          const merged = Array.from({ length: baseLength }, (_, idx) => {
            const def =
              idx < defaults.length
                ? defaults[idx]
                : {
                    id: idx,
                    label: `Link ${idx + 1}`,
                    url: '',
                    iconKey: 'open',
                    placement: 'both',
                    visibility: 'all',
                    visibilityEmails: '',
                  };
            const btn = buttons[idx] || {};
            const combined = { ...def, ...btn, id: idx };
            return {
              ...combined,
              placement: combined.placement || 'both',
              visibility: combined.visibility || 'all',
              visibilityEmails:
                typeof combined.visibilityEmails === 'string'
                  ? combined.visibilityEmails
                  : '',
              menuId: combined.menuId || 'Personalizzati',
            };
          });
          setCustomButtons(merged);
        } else {
          setCustomButtons(makeDefaultCustomButtons());
        }
      } catch (e) {
        console.error('loadCustomButtons error', e);
      }
    };
    loadCustomButtons();
  }, []);

  // Carica config pulsanti header
  useEffect(() => {
    const loadHeaderButtonsConfig = async () => {
      try {
        const ref = doc(db, 'settings', 'sidebarHeaderButtons');
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() || {};
          const cfg = data.config || {};
          setHeaderButtonsConfig({
            ...HEADER_BUTTON_DEFAULTS,
            ...cfg,
          });
        } else {
          setHeaderButtonsConfig(HEADER_BUTTON_DEFAULTS);
        }
      } catch (e) {
        console.error('loadHeaderButtonsConfig error', e);
      }
    };
    loadHeaderButtonsConfig();
  }, []);

  /* -------------------- STATIC folders targets -------------------- */
  const staticFolderTargets = useMemo(() => {
    return (Array.isArray(STATIC_LINKS) ? STATIC_LINKS : [])
      .map((blk) => ({ id: blk.id, title: blk.title }))
      .filter((b) => b.id && b.title);
  }, []);

  /* -------------------- Flat static entries -------------------- */
  const allStaticEntries = useMemo(() => {
    const rows = [];
    (Array.isArray(STATIC_LINKS) ? STATIC_LINKS : []).forEach((blk) => {
      rows.push({
        type: 'static',
        id: blk.id,
        title: (staticOverrides?.[blk.id]?.title) ?? blk.title,
        path: blk.path,
        external: blk.external,
        adminOnly: blk.adminOnly,
        icon: blk.icon,
        parentStaticId: null,
      });
      if (Array.isArray(blk.subLinks)) {
        blk.subLinks.forEach((s) => {
          rows.push({
            type: 'static-sub',
            id: s.id,
            title: (staticOverrides?.[s.id]?.title) ?? s.title,
            path: s.path,
            external: s.external,
            adminOnly: s.adminOnly || blk.adminOnly,
            icon: s.icon,
            parentStaticId: blk.id,
          });
        });
      }
    });
    return rows;
  }, [staticOverrides]);

  /* -------------------- Files assigned to static blocks ---------------- */
  const uploadsByStaticBlock = useMemo(() => {
    const map = {};
    uploads.forEach((u) => {
      const cid = u.categoryId || '';
      if (typeof cid === 'string' && cid.startsWith('static:')) {
        const sid = cid.slice('static:'.length);
        if (!map[sid]) map[sid] = [];
        map[sid].push(u);
      }
    });
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) =>
        (a.visualName || a.displayName || '').localeCompare(
          b.visualName || b.displayName || '',
          'it',
          { sensitivity: 'base' }
        )
      );
    });
    return map;
  }, [uploads]);

  /* -------------------- FILTERS & GROUPS -------------------- */
  const filteredUploads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return uploads
      .filter((it) => {
        if (!q) return true;
        return (
          matchQuery(it.visualName || it.displayName, q) ||
          (it.tags || []).some((t) => matchQuery(t, q))
        );
      })
      .sort((a, b) => {
        const ai = Number.isFinite(a.index) ? a.index : Number.MAX_SAFE_INTEGER;
        const bi = Number.isFinite(b.index) ? b.index : Number.MAX_SAFE_INTEGER;
        if (ai !== bi) return ai - bi;
        return (a.visualName || a.displayName || '').localeCompare(
          b.visualName || b.displayName || '',
          'it',
          { sensitivity: 'base' }
        );
      });
  }, [uploads, search]);

  const uploadsByFolder = useMemo(() => {
    const map = {};
    filteredUploads.forEach((u) => {
      const key = u.categoryId;
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(u);
    });
    return map;
  }, [filteredUploads]);

  const orphanFiles = useMemo(() => filteredUploads.filter((u) => !u.categoryId), [filteredUploads]);

  const rootFolders = useMemo(
    () => (customFolders || []).filter((f) => !f.parentId),
    [customFolders]
  );
  const childFoldersOf = useCallback(
    (parentId) => (customFolders || []).filter((f) => f.parentId === parentId).sort(sortFolders),
    [customFolders]
  );

  const mixedItems = useMemo(() => {
    const q = search.trim().toLowerCase();

    // Static visibili (con override)
    let visibleStatic = (Array.isArray(STATIC_LINKS) ? STATIC_LINKS : [])
      .filter((blk) => !blk.adminOnly || isAdmin(user))
      .map((blk) => {
        const overrideTitle = staticOverrides[blk.id]?.title;
        const base = overrideTitle ? { ...blk, title: overrideTitle } : blk;
        if (Array.isArray(base.subLinks)) {
          const sub = base.subLinks
            .filter((s) => !s.adminOnly || isAdmin(user))
            .map((s) => {
              const o = staticOverrides[s.id]?.title;
              return o ? { ...s, title: o } : s;
            });
          return { ...base, subLinks: sub };
        }
        return base;
      });

    // blocchi top-level spostati spariscono
    visibleStatic = visibleStatic.filter((blk) => {
      const cat = staticOverrides[blk.id]?.categoryId;
      return !cat;
    });

    const staticBlocks = visibleStatic
      .filter((blk) => {
        if (!q) return true;
        if (blk.title && matchQuery(blk.title, q)) return true;
        if (Array.isArray(blk.subLinks)) return blk.subLinks.some((s) => matchQuery(s.title, q));
        return false;
      })
      .map((blk) => ({ type: 'static', data: blk }));

    // Cartelle custom
    const folders = rootFolders
      .filter((f) => !q || matchQuery(f.label, q))
      .sort(sortFolders)
      .map((f) => ({ type: 'folder', data: f }));

    // Sezione "Da sistemare" solo per OWNER
    const orphansNode = ownerView && showOrphans && orphanFiles.length
      ? [{ type: 'orphans', data: { id: 'orphans', label: 'Da sistemare' } }]
      : [];

    return [...(orphansNode || []), ...staticBlocks, ...folders];
  }, [rootFolders, search, staticOverrides, user, ownerView, showOrphans, orphanFiles.length]);

  /* -------------------- APPROVALS -------------------- */
  const fetchPendingApprovals = useCallback(async () => {
    if (!canUseApprovals) {
      setPendingApprovals([]);
      setPendingApprovalsCount(0);
      return;
    }
    setApprovalsLoading(true);
    try {
      const col = collection(db, 'users');
      const qRef = query(col, where('status', '==', 'pending'));
      const snap = await getDocs(qRef);
      const rows = [];
      snap.forEach((d) => {
        const data = d.data() || {};
        rows.push({
          id: d.id,
          email: data.email || '',
          displayName: data.displayName || data.name || '',
          status: data.status || 'pending',
        });
      });
      setPendingApprovals(rows);
      setPendingApprovalsCount(rows.length);
    } catch (e) {
      console.error('fetchPendingApprovals', e);
      setPendingApprovals([]);
      setPendingApprovalsCount(0);
    } finally {
      setApprovalsLoading(false);
    }
  }, [canUseApprovals]);

  const openApprovalsDialog = useCallback(async () => {
    if (!canUseApprovals) return;
    setDlgApprovalsOpen(true);
    await fetchPendingApprovals();
  }, [canUseApprovals, fetchPendingApprovals]);

  const approveUser = async (uid) => {
    try {
      setApprovalsLoading(true);
      await updateDoc(doc(db, 'users', uid), {
        status: 'approved',
        approvedAt: new Date(),
      });
      await fetchPendingApprovals();
      setSnack({ open: true, type: 'success', msg: 'Utente approvato' });
    } catch (e) {
      console.error(e);
      setSnack({ open: true, type: 'error', msg: 'Errore durante l’approvazione' });
    } finally {
      setApprovalsLoading(false);
    }
  };


  // AppBar Highlight (promemoria per barra superiore in Dashboard)
  const openAppbarHighlightDialog = useCallback(async () => {
    if (!canEditAppbarHighlight) {
      setSnack({
        open: true,
        type: 'error',
        msg: 'Solo il responsabile o un admin possono modificare il promemoria nella barra superiore.',
      });
      return;
    }
    try {
      setBusy(true);
      const ref = doc(db, 'settings', 'appbarHighlight');
      const snap = await getDoc(ref);
      const cfg = (snap.exists() && (snap.data()?.config || {})) || {};
      setAppbarHighlightMode(cfg.mode || 'auto');
      setAppbarHighlightManualText(cfg.manualText || '');
      setAppbarHighlightManualIconKey(cfg.manualIconKey || 'star');
      setDlgAppbarHighlightOpen(true);
    } catch (e) {
      console.error('openAppbarHighlightDialog error', e);
      setSnack({
        open: true,
        type: 'error',
        msg: 'Errore nel caricamento del promemoria AppBar.',
      });
    } finally {
      setBusy(false);
    }
  }, [canEditAppbarHighlight, user]);

  const handleAppbarHighlightSave = useCallback(async () => {
    if (!canEditAppbarHighlight) return;
    try {
      setBusy(true);
      const ref = doc(db, 'settings', 'appbarHighlight');
      await setDoc(
        ref,
        {
          config: {
            mode: appbarHighlightMode,
            manualText: appbarHighlightManualText.trim(),
            manualIconKey: appbarHighlightManualIconKey || 'star',
          },
          updatedAt: serverTimestamp(),
          updatedBy: user ? user.email || user.uid : null,
        },
        { merge: true }
      );
      setSnack({
        open: true,
        type: 'success',
        msg: 'Promemoria AppBar aggiornato.',
      });
      setDlgAppbarHighlightOpen(false);
    } catch (e) {
      console.error('handleAppbarHighlightSave error', e);
      setSnack({
        open: true,
        type: 'error',
        msg: 'Errore nel salvataggio del promemoria AppBar.',
      });
    } finally {
      setBusy(false);
    }
  }, [canEditAppbarHighlight, appbarHighlightMode, appbarHighlightManualText, appbarHighlightManualIconKey, user]);

  /* -------------------- ACTIONS -------------------- */
  const handleUploadClick = () => {
    if (!user) {
      setSnack({ open: true, type: 'error', msg: 'Devi essere autenticato per caricare file.' });
      return;
    }
    setUploadMode('existing');
    setUploadSelectedFolder('');
    setUploadFile(null);
    setUploadNewFolderName('');
    setUploadNewFolderIconKey('folder');
    setUploadNewFolderVisibility('public');
    setNewUploadsBadge(0);
    localStorage.setItem(seenKey, String(uploads.length || 0));
    setDlgUploadOpen(true);
  };

  const onPickUploadFile = () => uploadInputRef.current?.click();
  const onUploadFileChange = (e) => setUploadFile(e.target.files?.[0] || null);

  const onConfirmUpload = async () => {
    if (!uploadFile) {
      setSnack({ open: true, type: 'error', msg: 'Seleziona un file da caricare.' });
      return;
    }

    try {
      setBusy(true);
      let destination = uploadSelectedFolder || null;

      if (uploadMode === 'new') {
        const name = (uploadNewFolderName || '').trim();
        if (!name) {
          setSnack({ open: true, type: 'error', msg: 'Inserisci il nome della nuova cartella.' });
          setBusy(false);
          return;
        }
        const iconKey = uploadNewFolderIconKey || 'folder';
        const visibility = uploadNewFolderVisibility || 'public';
        const created = await createCustomFolder({
          label: name,
          iconKey,
          visibility,
          parentId: null,
        });
        destination = created?.id || created;
        await refreshFolders();
      }

      if (!destination) {
        setSnack({ open: true, type: 'error', msg: 'Scegli una cartella di destinazione.' });
        setBusy(false);
        return;
      }

      const safe = makeStorageSafeName(uploadFile.name || 'file');
      const storagePath = `uploads/${safe}`;

      const meta = {
        storagePath,
        displayName: safeDisplay(safe),
        visualName: safeDisplay(safe),
        ownerUid: user.uid,
        tags: [],
        categoryId: destination,
      };

      const res = await uploadFileWithMetadata(uploadFile, meta);

      await upsertFileMeta({
        ...meta,
        size: res?.size || uploadFile.size || 0,
      });

      setSnack({ open: true, type: 'success', msg: 'Upload completato' });
      setDlgUploadOpen(false);

      await Promise.all([refreshUploads(), refreshUsage()]);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, type: 'error', msg: 'Errore durante l’upload' });
    } finally {
      setBusy(false);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
      setUploadFile(null);
    }
  };


  const openBackupDialog = async () => {
    setBackupDialogOpen(true);
    await refreshBackups();
  };

  const handleManualBackup = async () => {
    if (!user) {
      setSnack({ open: true, type: 'error', msg: 'Devi essere autenticato per creare un backup.' });
      return;
    }
    try {
      setBackupBusy(true);
      const runBackupFn = httpsCallable(functions, 'runFullBackup');
      const res = await runBackupFn({});
      const data = (res && res.data) || {};
      const storagePath = data.storagePath || data.path || '';
      let downloadUrl = data.downloadUrl || '';
      if (!downloadUrl && storagePath) {
        downloadUrl = await getDownloadURLByPath(storagePath);
      }
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
      setSnack({ open: true, type: 'success', msg: 'Backup completato.' });
      await Promise.all([refreshBackups(), refreshUsage()]);
    } catch (err) {
      console.error('handleManualBackup error', err);
      setSnack({ open: true, type: 'error', msg: 'Errore durante il backup.' });
    } finally {
      setBackupBusy(false);
    }
  };

  const handleDownloadBackup = async (backup) => {
    try {
      setBackupBusy(true);
      let url = backup.downloadUrl || '';
      if (!url && backup.storagePath) {
        url = await getDownloadURLByPath(backup.storagePath);
      }
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setSnack({ open: true, type: 'error', msg: 'Download non disponibile per questo backup.' });
      }
    } catch (err) {
      console.error('handleDownloadBackup error', err);
      setSnack({ open: true, type: 'error', msg: 'Errore durante il download del backup.' });
    } finally {
      setBackupBusy(false);
    }
  };

  const handleRestoreBackup = async (backup) => {
    if (!window.confirm('Sei sicuro di voler ripristinare questo backup? L’operazione potrebbe sovrascrivere i dati attuali.')) {
      return;
    }
    try {
      setBackupBusy(true);
      const restoreFn = httpsCallable(functions, 'restoreFullBackup');
      await restoreFn({ backupId: backup.id || null, storagePath: backup.storagePath || null });
      setSnack({ open: true, type: 'success', msg: 'Ripristino avviato. Potrebbe essere necessario qualche minuto.' });
    } catch (err) {
      console.error('handleRestoreBackup error', err);
      setSnack({ open: true, type: 'error', msg: 'Errore durante il ripristino del backup.' });
    } finally {
      setBackupBusy(false);
    }
  };

  const openMenu = (ev, target) => {
    ev.stopPropagation();
    ev.preventDefault();
    setMenuAnchor(ev.currentTarget);
    setMenuTarget(target);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuTarget(null);
  };

  const openEditItem = (payload) => {
    if (!payload) return;
    const { type, data } = payload;

    if (type === 'file') {
      const id = data.id || encodeURIComponent(data.storagePath || '');
      const label = data.visualName || data.displayName || data.name || '';
      const visibility = data.visibility || 'public';
      const moveTo = data.categoryId || '';
      setDlgEditItemData({ type, id, label, visibility, raw: data, moveTo, iconKey: 'folder' });
    } else if (type === 'static' || type === 'static-sub') {
      const id = data.id;
      const label = (staticOverrides?.[id]?.title) ?? data.title ?? '';
      const visibility = (staticOverrides?.[id]?.visibility) || 'public';
      const moveTo = (staticOverrides?.[id]?.categoryId) || '';
      setDlgEditItemData({ type, id, label, visibility, raw: data, moveTo, iconKey: 'folder' });
    } else if (type === 'folder') {
      const id = data.id;
      const label = data.label || '';
      const visibility = data.visibility || 'private';
      const moveTo = data.parentId || '';
      const iconKey = data.iconKey || 'folder';
      setDlgEditItemData({ type, id, label, visibility, raw: data, moveTo, iconKey });
    } else {
      return;
    }
    setDlgEditItemOpen(true);
    closeMenu();
  };

  const submitEditItem = async () => {
    const { type, id, label, visibility, moveTo, raw, iconKey } = dlgEditItemData;
    const name = String(label || '').trim();
    if (!id || !type || !name) return;

    try {
      setBusy(true);

      if (type === 'file') {
        const cat = moveTo || null;
        let ok = false;
        try { await renameFileVisual(id, name); ok = true; } catch { ok = false; }
        if (!ok && raw) {
          await upsertFileMeta({
            storagePath: raw.storagePath,
            displayName: raw.displayName || name,
            visualName: name,
            ownerUid: raw.ownerUid || user?.uid || null,
            tags: raw.tags || [],
            categoryId: cat,
            size: raw.size || 0,
            index: Number.isFinite(raw.index) ? raw.index : null,
          });
        } else {
          try {
            await setFileCategory(id, cat);
          } catch {
            await upsertFileMeta({
              storagePath: raw.storagePath,
              visualName: name,
              displayName: raw.displayName || name,
              tags: raw.tags || [],
              categoryId: cat,
              size: raw.size || 0,
            });
          }
        }
        await setDoc(doc(db, 'files', id), { visibility }, { merge: true });
        await refreshUploads();
        setSnack({ open: true, type: 'success', msg: 'File aggiornato' });
      }

      if (type === 'static' || type === 'static-sub') {
        const cat =
          typeof moveTo === 'string' && moveTo.startsWith('static:')
            ? moveTo.replace(/^static:/, '')
            : (moveTo || null);

        await setStaticLinkOverrideTitle(id, name);
        await setDoc(
          doc(db, 'staticLinkOverrides', id),
          { visibility, categoryId: cat, title: name },
          { merge: true }
        );
        setStaticOverrides((prev) => ({
          ...prev,
          [id]: { ...(prev?.[id] || {}), title: name, visibility, categoryId: cat },
        }));
        await refreshStaticOverrides();
        setSnack({ open: true, type: 'success', msg: 'Link aggiornato' });
      }

      if (type === 'folder') {
        await updateCustomFolder(
          id,
          {
            label: name,
            parentId: (moveTo && !String(moveTo).startsWith('static:')) ? moveTo : null,
            visibility,
            iconKey: iconKey || 'folder',
          }
        );
        await refreshFolders();
        setSnack({ open: true, type: 'success', msg: 'Cartella aggiornata' });
      }

      setDlgEditItemOpen(false);
    } catch (e) {
      console.error(e);
      setSnack({ open: true, type: 'error', msg: 'Errore aggiornamento' });
    } finally {
      setBusy(false);
    }
  };

  // Sposta rapido (solo file)
  const askMove = () => {
    if (menuTarget?.type === 'file') {
      setDlgMoveTargetFile(menuTarget.data);
      setMoveFilter('');
      setDlgMoveOpen(true);
      closeMenu();
    }
  };

  const doMove = async (folderId) => {
    try {
      if (!folderId) {
        setSnack({ open: true, type: 'error', msg: 'Scegli una cartella di destinazione.' });
        return;
      }
      setBusy(true);
      const idsToMove = selectedIds.size
        ? Array.from(selectedIds)
        : dlgMoveTargetFile
        ? [dlgMoveTargetFile.id]
        : (menuTarget?.type === 'file' ? [menuTarget.data.id] : []);

      const byId = {};
      uploads.forEach((u) => (byId[u.id] = u));

      await Promise.all(
        idsToMove.map(async (id) => {
          const f = byId[id];
          try {
            await setFileCategory(id, folderId);
          } catch {
            if (f?.storagePath) {
              await upsertFileMeta({
                storagePath: f.storagePath,
                visualName: f.visualName || f.displayName || 'Documento',
                displayName: f.displayName || f.visualName || 'Documento',
                tags: f.tags || [],
                categoryId: folderId,
                size: f.size || 0,
              });
            } else {
              throw new Error('missing storagePath');
            }
          }
        })
      );

      setSnack({ open: true, type: 'success', msg: 'Spostato correttamente' });
      await refreshUploads();
    } catch (e) {
      console.error(e);
      setSnack({ open: true, type: 'error', msg: 'Errore nello spostamento' });
    } finally {
      setBusy(false);
      setDlgMoveOpen(false);
      setDlgMoveTargetFile(null);
      setSelectedIds(new Set());
      setMultiMode(false);
    }
  };

  const askTags = () => {
    if (menuTarget?.type !== 'file') return;
    setDlgTagsValue((menuTarget.data.tags || []).join(', '));
    setDlgTagsOpen(true);
    closeMenu();
  };

  const doSaveTags = async () => {
    try {
      setBusy(true);
      const raw = String(dlgTagsValue || '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const idsToTag = selectedIds.size
        ? Array.from(selectedIds)
        : menuTarget?.data?.id
        ? [menuTarget.data.id]
        : [];

      const byId = {};
      uploads.forEach((u) => (byId[u.id] = u));

      await Promise.all(
        idsToTag.map(async (id) => {
          try {
            await setFileTags(id, raw);
          } catch {
            const f = byId[id];
            if (f?.storagePath) {
              await upsertFileMeta({
                storagePath: f.storagePath,
                visualName: f.visualName || f.displayName || 'Documento',
                displayName: f.displayName || f.visualName || 'Documento',
                tags: raw,
                categoryId: f.categoryId || null,
                size: f.size || 0,
              });
            } else {
              throw new Error('missing storagePath');
            }
          }
        })
      );

      setSnack({ open: true, type: 'success', msg: 'Tag aggiornati' });
      await refreshUploads();
    } catch (e) {
      console.error(e);
      setSnack({ open: true, type: 'error', msg: 'Impossibile aggiornare i tag' });
    } finally {
      setBusy(false);
      setDlgTagsOpen(false);
      setSelectedIds(new Set());
      setMultiMode(false);
    }
  };

  // APRI FILE
  const onOpenFile = async (file) => {
    try {
      let url = file.downloadURL;
      if (!url) url = await getDownloadURLByPath(file.storagePath);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      handleClose();
    } catch (e) {
      console.error('open file failed', e);
      setSnack({ open: true, type: 'error', msg: 'Impossibile aprire il file' });
    }
  };

  const doDelete = async () => {
    const tgt = menuTarget;
    if (!tgt) return;
    closeMenu();

    const ok = window.confirm('Confermi l’eliminazione?');
    if (!ok) return;

    try {
      setBusy(true);
      if (tgt.type === 'file') {
        if (tgt.data?.storagePath) {
          await deleteFile(tgt.data.storagePath);
        }
        await Promise.all([refreshUploads(), refreshUsage()]);
      } else if (tgt.type === 'folder') {
        await deleteCustomFolder(tgt.data.id);
        await refreshFolders();
      }
      setSnack({ open: true, type: 'success', msg: 'Eliminato' });
    } catch (e) {
      console.error(e);
      setSnack({ open: true, type: 'error', msg: 'Impossibile eliminare' });
    } finally {
      setBusy(false);
    }
  };

  /* -------------------- MULTISELECT & DND -------------------- */
  const toggleSelect = (id, additive = false, rangeAnchorId = null) => {
    setSelectedIds((prev) => {
      const next = new Set(additive ? prev : []);
      if (rangeAnchorId && uploads.length) {
        const ids = uploads.map((u) => u.id);
        const a = ids.indexOf(rangeAnchorId);
        const b = ids.indexOf(id);
        if (a >= 0 && b >= 0) {
          const [s, e] = a < b ? [a, b] : [b, a];
          for (let i = s; i <= e; i++) next.add(ids[i]);
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleFileRowClick = (e, file) => {
    if (multiMode || e.ctrlKey || e.metaKey || e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      setMultiMode(true);
      toggleSelect(
        file.id,
        e.ctrlKey || e.metaKey,
        e.shiftKey ? (selectedIds.values().next().value || file.id) : null
      );
      return;
    }
    onOpenFile(file);
  };

  const handleDragStart = (e, file) => {
    const ids = selectedIds.size ? Array.from(selectedIds) : [file.id];
    e.dataTransfer.setData('application/x-ids', JSON.stringify(ids));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFolderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleFolderDrop = async (e, folderId) => {
    e.preventDefault();
    try {
      const data = e.dataTransfer.getData('application/x-ids');
      const ids = data ? JSON.parse(data) : [];
      if (!Array.isArray(ids) || !ids.length) return;

      setSelectedIds(new Set(ids));
      await doMove(folderId);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, type: 'error', msg: 'Errore nello spostamento' });
    }
  };

  /* ---------- HANDLER PULSANTI PERSONALIZZATI ---------- */

  const handleCustomButtonClick = (arg) => {
    const btn =
      typeof arg === 'number'
        ? customButtons[arg]
        : arg && typeof arg === 'object'
        ? arg
        : null;
    if (!btn || !btn.url) return;
    const raw = String(btn.url || '').trim();
    if (!raw) return;
    const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const openCustomButtonDialog = (index) => {
    const isNew = index == null || index < 0 || !customButtons[index];
    const idx = isNew ? customButtons.length : index;
    const defaults = makeDefaultCustomButtons();
    const btn =
      customButtons[idx] ||
      defaults[idx] || {
        id: idx,
        label: `Link ${idx + 1}`,
        url: '',
        iconKey: 'open',
        placement: 'both',
        visibility: 'all',
        visibilityEmails: '',
      };
    setEditingCustomIndex(idx);
    setCustomBtnForm({
      label: btn.label || `Link ${idx + 1}`,
      url: btn.url || '',
      iconKey: btn.iconKey || 'open',
      placement: btn.placement || 'both',
      visibility: btn.visibility || 'all',
      visibilityEmails: btn.visibilityEmails || '',
      menuId: btn.menuId || 'Personalizzati',
    });
    setCustomBtnDialogOpen(true);
  };

  const handleCustomButtonSave = async () => {
    if (editingCustomIndex == null) {
      setCustomBtnDialogOpen(false);
      return;
    }
    const idx = editingCustomIndex;
    const next = [...customButtons];
    next[idx] = {
      ...(next[idx] || {}),
      id: idx,
      label: (customBtnForm.label || '').trim() || `Link ${idx + 1}`,
      url: (customBtnForm.url || '').trim(),
      iconKey: customBtnForm.iconKey || 'open',
      placement: customBtnForm.placement || 'both',
      visibility: customBtnForm.visibility || 'all',
      visibilityEmails: customBtnForm.visibilityEmails || '',
      menuId: customBtnForm.menuId || 'Personalizzati',
    };
    setCustomButtons(next);
    try {
      await setDoc(
        doc(db, 'settings', 'sidebarCustomButtons'),
        {
          buttons: next,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || '',
        },
        { merge: true }
      );
      setSnack({ open: true, type: 'success', msg: 'Pulsante aggiornato' });
    } catch (e) {
      console.error('handleCustomButtonSave error', e);
      setSnack({ open: true, type: 'error', msg: 'Errore salvataggio pulsante' });
    } finally {
      setCustomBtnDialogOpen(false);
      setEditingCustomIndex(null);
    }
  };

  const handleCustomButtonDelete = async () => {
    if (editingCustomIndex == null) return;
    const idx = editingCustomIndex;
    const defaults = makeDefaultCustomButtons();
    const next = [...customButtons];
    next[idx] = defaults[idx] || {
      id: idx,
      label: `Link ${idx + 1}`,
      url: '',
      iconKey: 'open',
      placement: 'both',
      visibility: 'all',
      visibilityEmails: '',
      menuId: 'Personalizzati',
    };
    setCustomButtons(next);
    try {
      await setDoc(
        doc(db, 'settings', 'sidebarCustomButtons'),
        {
          buttons: next,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || '',
        },
        { merge: true }
      );
      setSnack({ open: true, type: 'success', msg: 'Pulsante eliminato' });
    } catch (e) {
      console.error('handleCustomButtonDelete error', e);
      setSnack({ open: true, type: 'error', msg: 'Errore eliminazione pulsante' });
    } finally {
      setCustomBtnDialogOpen(false);
      setEditingCustomIndex(null);
    }
  };

  const handleCustomButtonTestOpen = () => {
    if (editingCustomIndex == null) return;
    const raw = (customBtnForm.url || '').trim();
    if (!raw) return;
    const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  /* ---------- CONFIGURAZIONE PULSANTI HEADER ---------- */

  const openHeaderButtonsConfigDialog = () => {
    if (!ownerView) return;
    setHeaderButtonsConfigDraft(headerButtonsConfig);
    setHeaderButtonsConfigDialogOpen(true);
  };

  const handleHeaderButtonConfigFieldChange = (id, field, value) => {
    setHeaderButtonsConfigDraft((prev) => {
      const base = getHeaderButtonConfig(prev, id);
      return {
        ...prev,
        [id]: {
          ...base,
          [field]: value,
        },
      };
    });
  };

  const handleHeaderButtonsConfigSave = async () => {
    setHeaderButtonsConfig(headerButtonsConfigDraft);
    try {
      await setDoc(
        doc(db, 'settings', 'sidebarHeaderButtons'),
        {
          config: headerButtonsConfigDraft,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || '',
        },
        { merge: true }
      );
      setSnack({ open: true, type: 'success', msg: 'Configurazione pulsanti aggiornata' });
    } catch (e) {
      console.error('handleHeaderButtonsConfigSave error', e);
      setSnack({ open: true, type: 'error', msg: 'Errore salvataggio configurazione pulsanti' });
    } finally {
      setHeaderButtonsConfigDialogOpen(false);
    }
  };

  /* -------------------- RENDERERS -------------------- */
  const renderUploadRow = (file) => {
    const label = safeDisplay(file.visualName || file.displayName || 'Documento');
    const selected = selectedIds.has(file.id);

    return (
      <ListItem
        key={`file-${file.id}`}
        className={`nested-link ${selected ? 'is-selected' : ''}`}
        button
        draggable
        onDragStart={(e) => handleDragStart(e, file)}
        onClick={(e) => handleFileRowClick(e, file)}
      >
        <IconButton
          size="small"
          onClick={(e) => openMenu(e, { type: 'file', data: file })}
          className="kebab"
        >
          <MoreVert fontSize="small" />
        </IconButton>

        {selected && (
          <ListItemIcon className="sidebar-icon selection-mark">
            <SelectedIcon fontSize="small" />
          </ListItemIcon>
        )}

        <ListItemText
          primary={<span className="file-name-ellipsis">{label}</span>}
          secondary={file.size ? human(file.size) : null}
        />
      </ListItem>
    );
  };

  const renderStaticRowInFolder = (entry) => {
    const label = safeDisplay(entry.title || 'Link');
    const isExternal = !!entry.external;
    const props = isExternal
      ? { component: 'a', href: entry.path, target: '_blank', rel: 'noopener noreferrer' }
      : { component: Link, to: entry.path || '#' };

    return (
      <ListItem
        key={`staticin-${entry.id}`}
        className="nested-link"
        button
        {...props}
        onClick={handleClose}
      >
        <IconButton
          size="small"
          onClick={(e) => openMenu(e, { type: entry.type, data: { id: entry.id, title: entry.title, path: entry.path, external: entry.external } })}
          className="kebab"
        >
          <MoreVert fontSize="small" />
        </IconButton>
        <ListItemText primary={<Typography sx={{ ...oneLine }}>{label}</Typography>} />
      </ListItem>
    );
  };

  const renderFolderChildren = (parentId) => {
    const kids = childFoldersOf(parentId);
    if (!kids.length) return null;
    return (
      <List component="div" disablePadding>
        {kids.map((cf) => renderCustomFolder(cf))}
      </List>
    );
  };

  const renderCustomFolder = (f) => {
    const folderId = f.id;
    const open = !!expanded[folderId];
    const toggle = () => setExpanded((prev) => ({ ...prev, [folderId]: !prev[folderId] }));
    const canManage = user && (isAdmin(user) || f.ownerUid === user.uid || isOwner(user));

    const assignedStatics = (allStaticEntries || [])
      .filter((e) => (staticOverrides?.[e.id]?.categoryId || null) === folderId && (!e.adminOnly || isAdmin(user)));

    return (
      <div
        key={`folder-${folderId}`}
        onDragOver={handleFolderDragOver}
        onDrop={(e) => handleFolderDrop(e, folderId)}
      >
        <ListItem button onClick={toggle} className="sidebar-item" sx={{ minHeight: 44 }}>
          <IconButton
            size="small"
            onClick={(e) => canManage && openMenu(e, { type: 'folder', data: f })}
            className="kebab"
            style={{ visibility: canManage ? 'visible' : 'hidden' }}
            aria-label="Menu cartella"
          >
            <MoreVert fontSize="small" />
          </IconButton>

          <ListItemIcon className="sidebar-icon">
            <span className="folder-icon-box">
              {renderFolderIconByKey(f.iconKey)}
            </span>
          </ListItemIcon>

          <ListItemText
            primary={
              <Typography sx={{ ...oneLine, pr: 3 }}>
                {f.label || 'Cartella'}
              </Typography>
            }
          />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItem>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {assignedStatics.map((e) => renderStaticRowInFolder(e))}
            {(uploadsByFolder[folderId] || []).map((u) => renderUploadRow(u))}
          </List>
          {renderFolderChildren(folderId)}
        </Collapse>
      </div>
    );
  };

  const handleStaticClickClose = () => { handleClose(); };

  const renderStaticBlock = (blk) => {
    if (blk.adminOnly && !isAdmin(user)) return null;

    const hasSub = Array.isArray(blk.subLinks) && blk.subLinks.length > 0;
    const open = !!expanded[blk.id];
    const toggle = () => setExpanded((prev) => ({ ...prev, [blk.id]: !prev[blk.id] }));

    const staticFiles = uploadsByStaticBlock[blk.id] || [];

    return (
      <div key={`static-${blk.id}`}>
        <ListItem
          button
          onClick={hasSub ? toggle : handleStaticClickClose}
          component={!hasSub ? (blk.external ? 'a' : Link) : 'div'}
          to={!hasSub && !blk.external ? blk.path || '#' : undefined}
          href={!hasSub && blk.external ? blk.path : undefined}
          target={!hasSub && blk.external ? '_blank' : undefined}
          rel={!hasSub && blk.external ? 'noopener noreferrer' : undefined}
          className="sidebar-item"
          sx={{ minHeight: 44 }}
        >
          <IconButton
            size="small"
            onClick={(e) => openMenu(e, { type: 'static', data: blk })}
            className="kebab"
          >
            <MoreVert fontSize="small" />
          </IconButton>

          <ListItemIcon className="sidebar-icon">
            <span className="folder-icon-box">
              {blk.icon || <OpenInNew />}
            </span>
          </ListItemIcon>

          <ListItemText
            primary={
              <Typography sx={{ ...oneLine, pr: 3 }}>
                {blk.title}
              </Typography>
            }
          />
          {!hasSub ? null : open ? <ExpandLess /> : <ExpandMore />}
        </ListItem>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {staticFiles.map((u) => renderUploadRow(u))}
            {hasSub &&
              blk.subLinks
                .filter((s) => !s.adminOnly || isAdmin(user))
                .filter((s) => !staticOverrides[s.id]?.categoryId)
                .map((s) => (
                  <ListItem
                    key={s.id}
                    button
                    className="nested-link"
                    component={s.external ? 'a' : Link}
                    to={!s.external ? s.path || '#' : undefined}
                    href={s.external ? s.path : undefined}
                    target={s.external ? '_blank' : undefined}
                    rel={s.external ? 'noopener noreferrer' : undefined}
                    onClick={handleStaticClickClose}
                    sx={{ minHeight: 36 }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => openMenu(e, { type: 'static-sub', data: s })}
                      className="kebab"
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                    <ListItemText
                      primary={
                        <Typography sx={{ ...oneLine }}>
                          {s.title}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
          </List>
        </Collapse>
      </div>
    );
  };

  const renderOrphans = () => {
    const open = !!expanded['orphans'];
    const toggle = () => setExpanded((prev) => ({ ...prev, orphans: !prev['orphans'] }));

    return (
      <div
        key="orphans"
        onDragOver={handleFolderDragOver}
        onDrop={(e) => handleFolderDrop(e, null)}
      >
        <ListItem button onClick={toggle} className="sidebar-item" sx={{ bgcolor: 'warning.light', minHeight: 44 }}>
          <ListItemIcon className="sidebar-icon">
            <FixIcon />
          </ListItemIcon>
          <ListItemText
            primary={<Typography sx={{ ...oneLine }}>Da sistemare</Typography>}
            secondary={<Typography variant="caption">{orphanFiles.length} file senza cartella</Typography>}
          />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {orphanFiles.map((u) => renderUploadRow(u))}
          </List>
        </Collapse>
      </div>
    );
  };

  const renderHeader = () => (
    <div className="sidebar-header">
      <IconButton className="close-btn" onClick={handleClose} size="small" aria-label="Chiudi sidebar">
        <CloseIcon />
      </IconButton>
      <img src={OG_IMG} alt="logo" className="sidebar-logo" />
      {busy && <LinearProgress className="busybar" />}
    </div>
  );

  /* ----------- SELECT destinazione cartella ----------- */
  const FolderSelect = ({ label, value, onChange, excludeId }) => {
    const itemsCustom = (customFolders || [])
      .filter((f) => !excludeId || f.id !== excludeId)
      .sort(sortFolders);

    const itemsStatic = staticFolderTargets;

    return (
      <TextField
        select
        fullWidth
        label={label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || '')}
        SelectProps={{ native: false, MenuProps: { PaperProps: { style: { maxHeight: 320 } } } }}
        sx={{ mb: 2 }}
      >
        {/* Cartelle personalizzate */}
        {itemsCustom.map((f) => (
          <MenuItem key={f.id} value={f.id} sx={{ minHeight: 36 }}>
            <Box sx={{ ...oneLine }}>
              <span style={{ display: 'inline-flex' }}>
                <span className="folder-icon-box">
                  {renderFolderIconByKey(f.iconKey)}
                </span>
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.label}</span>
            </Box>
          </MenuItem>
        ))}
        {/* Cartelle statiche (solo titolo) */}
        {itemsStatic.map((sf) => (
          <MenuItem key={`static:${sf.id}`} value={`static:${sf.id}`} sx={{ minHeight: 36 }}>
            <Box sx={{ ...oneLine }}>
              <span className="folder-icon-box">
                <OpenInNew fontSize="small" />
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{sf.title}</span>
            </Box>
          </MenuItem>
        ))}
      </TextField>
    );
  };

  /* ----------- ICON PICKER VISUALE ----------- */
  const IconPicker = ({ value, onChange }) => {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 2 }}>
        {FOLDER_ICONS.map((fi) => {
          const selected = value === fi.key;
          return (
            <Box
              key={fi.key}
              onClick={() => onChange(fi.key)}
              sx={{
                border: selected ? '2px solid' : '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                borderRadius: 1,
                cursor: 'pointer',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 42,
                userSelect: 'none',
              }}
              title={fi.key}
            >
              {fi.icon}
            </Box>
          );
        })}
      </Box>
    );
  };

  /* ----------- ICON PICKER per i pulsanti personalizzati ----------- */
  const CustomButtonIconPicker = ({ value, onChange }) => {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 1,
          mb: 2,
        }}
      >
        {CUSTOM_BUTTON_ICONS.map((ci) => {
          const selected = value === ci.key;
          return (
            <Box
              key={ci.key}
              onClick={() => onChange(ci.key)}
              sx={{
                border: selected ? '2px solid' : '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                borderRadius: 1,
                cursor: 'pointer',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 42,
                userSelect: 'none',
              }}
              title={ci.label}
            >
              {ci.icon}
            </Box>
          );
        })}
      </Box>
    );
  };

  /* ----------- SEARCH + ACTION BUTTONS ----------- */
  const renderSearchAndActions = () => {
    const reportsAllowed = canUseReports;
    const approvalsAllowed = canUseApprovals;

    const btns = [];
    const adminMenuItems = [];
    const linksMenuItems = [];
    const customMenuItems = [];

    // APPROVALS (Admin)
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'approvals');
      const rawEmails = String(cfg.visibilityEmails || '');
      const emailList = rawEmails
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean);
      const userEmail = (user?.email || '').toLowerCase();
      const emailAllowed = !!userEmail && emailList.includes(userEmail);

      const canSee =
        emailAllowed ||
        (approvalsAllowed &&
          canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails));
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        if (showInline) {
          btns.push(
            <Tooltip title={cfg.label || 'Approva iscrizioni'} key="btn-approvals">
              <span>
                <IconButton
                  className="action-btn compact"
                  size="small"
                  onClick={openApprovalsDialog}
                  aria-label={cfg.label || 'Approva iscrizioni'}
                  sx={{ p: 0.5 }}
                >
                  <Badge
                    color="error"
                    overlap="circular"
                    badgeContent={pendingApprovalsCount || 0}
                    invisible={!pendingApprovalsCount}
                  >
                    {renderCustomButtonIcon(cfg.iconKey)}
                  </Badge>
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          adminMenuItems.push(
            <MenuItem
              key="m-admin-approvals"
              onClick={() => {
                openApprovalsDialog();
                setAdminMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey)}</ListItemIcon>
              <ListItemText primary={cfg.label || 'Approva iscrizioni'} />
            </MenuItem>
          );
        }
      }
    }

    // OWNER: toggle orfani (Admin)
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'orphans');
      const rawEmails = String(cfg.visibilityEmails || '');
      const emailList = rawEmails
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean);
      const userEmail = (user?.email || '').toLowerCase();
      const emailAllowed = !!userEmail && emailList.includes(userEmail);

      const canSee =
        emailAllowed ||
        (ownerView &&
          canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails));
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        if (showInline) {
          btns.push(
            <Tooltip title={cfg.label || 'File da sistemare'} key="btn-orphans">
              <span>
                <IconButton
                  className="action-btn compact"
                  size="small"
                  onClick={() => setShowOrphans((v) => !v)}
                  aria-label={cfg.label || 'File da sistemare'}
                  sx={{ p: 0.5 }}
                >
                  {renderCustomButtonIcon(cfg.iconKey)}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          adminMenuItems.push(
            <MenuItem
              key="m-admin-orphans"
              onClick={() => {
                setShowOrphans((v) => !v);
                setAdminMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey)}</ListItemIcon>
              <ListItemText primary={cfg.label || 'File da sistemare'} />
            </MenuItem>
          );
        }
      }
    }

    // NOTE (Admin)
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'notes');
      const canSee = canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails);
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        if (showInline) {
          btns.push(
            <Tooltip title={cfg.label || 'I miei appunti'} key="btn-notes">
              <span>
                <IconButton
                  className="action-btn compact"
                  onClick={() => setNotesOpen(true)}
                  aria-label={cfg.label || 'I miei appunti'}
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  {renderCustomButtonIcon(cfg.iconKey)}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          adminMenuItems.push(
            <MenuItem
              key="m-admin-notes"
              onClick={() => {
                setNotesOpen(true);
                setAdminMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey)}</ListItemIcon>
              <ListItemText primary={cfg.label || 'I miei appunti'} />
            </MenuItem>
          );
        }
      }
    }

    // NUOVA CARTELLA (Admin)
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'newFolder');
      const canSee = canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails);
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        if (showInline) {
          btns.push(
            <Tooltip title={cfg.label || 'Nuova cartella'} key="btn-newfolder">
              <span>
                <IconButton
                  className="action-btn compact"
                  onClick={() => setDlgNewFolderOpen(true)}
                  aria-label={cfg.label || 'Nuova cartella'}
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  {renderCustomButtonIcon(cfg.iconKey)}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          adminMenuItems.push(
            <MenuItem
              key="m-admin-newfolder"
              onClick={() => {
                setDlgNewFolderOpen(true);
                setAdminMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey)}</ListItemIcon>
              <ListItemText primary={cfg.label || 'Nuova cartella'} />
            </MenuItem>
          );
        }
      }
    }


    // PROMEMORIA APPBAR (Admin/Owner)
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'appbarHighlight');
      const canSee = canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails) && canEditAppbarHighlight;
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        if (showInline) {
          btns.push(
            <Tooltip title={cfg.label || 'Promemoria AppBar'} key="btn-appbar-highlight">
              <span>
                <IconButton
                  className="action-btn compact"
                  onClick={openAppbarHighlightDialog}
                  aria-label={cfg.label || 'Promemoria AppBar'}
                  size="small"
                  sx={{ p: 0.5 }}
                  disabled={!canEditAppbarHighlight}
                >
                  {renderCustomButtonIcon(cfg.iconKey || 'star')}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          adminMenuItems.push(
            <MenuItem
              key="m-admin-appbar-highlight"
              onClick={() => {
                openAppbarHighlightDialog();
                setAdminMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey || 'star')}</ListItemIcon>
              <ListItemText primary={cfg.label || 'Promemoria AppBar'} />
            </MenuItem>
          );
        }
      }
    }

    
    // CLASSI & STUDENTI (Admin)
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'classStudents');
      const canSee = canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails);
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        if (showInline) {
          btns.push(
            <Tooltip title={cfg.label || 'Classi & Studenti'} key="btn-class-students">
              <span>
                <IconButton
                  className="action-btn compact"
                  size="small"
                  onClick={() => setClassStudentsOpen(true)}
                  aria-label={cfg.label || 'Classi & Studenti'}
                  sx={{ p: 0.5 }}
                >
                  {renderCustomButtonIcon(cfg.iconKey)}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          adminMenuItems.push(
            <MenuItem
              key="m-admin-class-students"
              onClick={() => {
                setClassStudentsOpen(true);
                setAdminMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey)}</ListItemIcon>
              <ListItemText primary={cfg.label || 'Classi & Studenti'} />
            </MenuItem>
          );
        }
      }
    }

// UPLOAD (Admin)
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'upload');
      const canSee = canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails);
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        if (showInline) {
          btns.push(
            <Tooltip title={cfg.label || 'Carica file'} key="btn-upload">
              <span>
                <IconButton
                  className="action-btn compact"
                  onClick={handleUploadClick}
                  disabled={!user || busy}
                  aria-label={cfg.label || 'Carica file'}
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  <Badge
                    color="error"
                    overlap="circular"
                    badgeContent={newUploadsBadge || 0}
                    invisible={!newUploadsBadge}
                  >
                    {renderCustomButtonIcon(cfg.iconKey)}
                  </Badge>
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          adminMenuItems.push(
            <MenuItem
              key="m-admin-upload"
              onClick={() => {
                handleUploadClick();
                setAdminMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey)}</ListItemIcon>
              <ListItemText primary={cfg.label || 'Carica file'} />
            </MenuItem>
          );
        }
      }
    }

    // FOGLIETTO (Links) — URL personalizzabile
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'foglietto');
      const canSee = canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails);
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        const openFoglietto = () => {
          const raw = (cfg.url || 'https://www.redentoremonserrato.it/foglietto.html').trim();
          const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
          window.open(href, '_blank', 'noopener,noreferrer');
        };

        if (showInline) {
          btns.push(
            <Tooltip
              title={cfg.label || 'Foglietto parrocchiale'}
              key="btn-foglietto"
            >
              <span>
                <IconButton
                  className="action-btn compact"
                  onClick={openFoglietto}
                  aria-label={cfg.label || 'Foglietto parrocchiale'}
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  {renderCustomButtonIcon(cfg.iconKey)}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          linksMenuItems.push(
            <MenuItem
              key="m-links-foglietto"
              onClick={() => {
                openFoglietto();
                setLinksMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey)}</ListItemIcon>
              <ListItemText primary={cfg.label || 'Foglietto parrocchiale'} />
            </MenuItem>
          );
        }
      }
    }

    // RESET PRIVACY UTENTE (Admin)
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'privacyReset');
      const rawEmails = String(cfg.visibilityEmails || '');
      const emailList = rawEmails
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean);
      const userEmail = (user?.email || '').toLowerCase();
      const emailAllowed = !!userEmail && emailList.includes(userEmail);

      const canSee =
        emailAllowed ||
        (canEditPrivacy &&
          canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails));
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        if (showInline) {
          btns.push(
            <Tooltip title={cfg.label || 'Reset privacy utente'} key="btn-privacy-reset">
              <span>
                <IconButton
                  className="action-btn compact"
                  onClick={openPrivacyResetDialog}
                  aria-label={cfg.label || 'Reset privacy utente'}
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  {renderCustomButtonIcon(cfg.iconKey)}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          adminMenuItems.push(
            <MenuItem
              key="m-admin-privacy-reset"
              onClick={() => {
                openPrivacyResetDialog();
                setAdminMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey)}</ListItemIcon>
              <ListItemText primary={cfg.label || 'Reset privacy utente'} />
            </MenuItem>
          );
        }
      }
    }

    // TURNI (Links)
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'turni');
      const canSee = canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails);
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        if (showInline) {
          btns.push(
            <Tooltip title={cfg.label || 'Turni'} key="btn-turni">
              <span>
                <IconButton
                  className="action-btn compact"
                  onClick={() =>
                    window.open(
                      'https://www.redentoremonserrato.it/turni.pdf',
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                  aria-label={cfg.label || 'Turni'}
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  {renderCustomButtonIcon(cfg.iconKey)}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          linksMenuItems.push(
            <MenuItem
              key="m-links-turni"
              onClick={() => {
                window.open(
                  'https://www.redentoremonserrato.it/turni.pdf',
                  '_blank',
                  'noopener,noreferrer'
                );
                setLinksMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey)}</ListItemIcon>
              <ListItemText primary={cfg.label || 'Turni'} />
            </MenuItem>
          );
        }
      }
    }

    // EDITOR PRIVACY (Admin)
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'privacyEditor');
      const rawEmails = String(cfg.visibilityEmails || '');
      const emailList = rawEmails
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean);
      const userEmail = (user?.email || '').toLowerCase();
      const emailAllowed = !!userEmail && emailList.includes(userEmail);

      const canSee =
        emailAllowed ||
        (canEditPrivacy &&
          canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails));
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        if (showInline) {
          btns.push(
            <Tooltip title={cfg.label || 'Editor Privacy'} key="btn-privacy-editor">
              <span>
                <IconButton
                  className="action-btn compact"
                  size="small"
                  onClick={openPrivacyEditor}
                  aria-label={cfg.label || 'Editor Privacy'}
                  sx={{ p: 0.5 }}
                >
                  {renderCustomButtonIcon(cfg.iconKey)}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          adminMenuItems.push(
            <MenuItem
              key="m-admin-privacy-editor"
              onClick={() => {
                openPrivacyEditor();
                setAdminMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey)}</ListItemIcon>
              <ListItemText primary={cfg.label || 'Editor Privacy'} />
            </MenuItem>
          );
        }
      }
    }

    // BACKUP & RIPRISTINO (Admin)
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'backup');
      const rawEmails = String(cfg.visibilityEmails || '');
      const emailList = rawEmails
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean);
      const userEmail = (user?.email || '').toLowerCase();
      const emailAllowed = !!userEmail && emailList.includes(userEmail);

      const canSee =
        emailAllowed || canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails);
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        if (showInline) {
          btns.push(
            <Tooltip title={cfg.label || 'Backup & Ripristino'} key="btn-backup">
              <span>
                <IconButton
                  className="action-btn compact"
                  size="small"
                  onClick={openBackupDialog}
                  aria-label={cfg.label || 'Backup & Ripristino'}
                  sx={{ p: 0.5 }}
                >
                  {renderCustomButtonIcon(cfg.iconKey)}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          adminMenuItems.push(
            <MenuItem
              key="m-admin-backup"
              onClick={() => {
                openBackupDialog();
                setAdminMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey)}</ListItemIcon>
              <ListItemText primary={cfg.label || 'Backup & Ripristino'} />
            </MenuItem>
          );
        }
      }
    }

    // REPORTS (Admin)
    {
      const cfg = getHeaderButtonConfig(headerButtonsConfig, 'reports');
      const rawEmails = String(cfg.visibilityEmails || '');
      const emailList = rawEmails
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean);
      const userEmail = (user?.email || '').toLowerCase();
      const emailAllowed = !!userEmail && emailList.includes(userEmail);

      const canSee =
        emailAllowed ||
        (reportsAllowed &&
          canSeeByVisibility(cfg.visibility, user, ownerView, cfg.visibilityEmails));
      if (canSee) {
        const showInline = cfg.placement === 'inline' || cfg.placement === 'both';
        const showInMenu = cfg.placement === 'menu' || cfg.placement === 'both';

        if (showInline) {
          btns.push(
            <Tooltip title={cfg.label || 'Reports'} key="btn-reports">
              <span>
                <IconButton
                  className="action-btn compact"
                  size="small"
                  onClick={() =>
                    window.open(
                      'https://catechismo.redentoremonserrato.it/reports',
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                  aria-label={cfg.label || 'Reports'}
                  sx={{ p: 0.5 }}
                >
                  {renderCustomButtonIcon(cfg.iconKey)}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (showInMenu) {
          adminMenuItems.push(
            <MenuItem
              key="m-admin-reports"
              onClick={() => {
                window.open(
                  'https://catechismo.redentoremonserrato.it/reports',
                  '_blank',
                  'noopener,noreferrer'
                );
                setAdminMenuAnchor(null);
              }}
            >
              <ListItemIcon>{renderCustomButtonIcon(cfg.iconKey)}</ListItemIcon>
              <ListItemText primary={cfg.label || 'Reports'} />
            </MenuItem>
          );
        }
      }
    }

    // Configurazione layout pulsanti header (solo owner)
    if (ownerView) {
      adminMenuItems.push(
        <MenuItem
          key="m-admin-header-config"
          onClick={() => {
            openHeaderButtonsConfigDialog();
            setAdminMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <FixIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Configura pulsanti header" />
        </MenuItem>
      );
    }

    // 5 pulsanti personalizzabili
    const customMenuGroups = {};

    customButtons.forEach((btn, idx) => {
      const title = btn.label || `Link ${idx + 1}`;
      const placement = btn.placement || 'both';
      const visibility = btn.visibility || 'all';

      const canSee = canSeeByVisibility(
        visibility,
        user,
        ownerView,
        btn.visibilityEmails
      );

      if (!canSee) return;

      const showInline =
        placement === 'inline' || placement === 'both';

      const showInMenu =
        placement === 'menu' || placement === 'both';

      if (showInline && btn.url) {
        btns.push(
          <Tooltip
            title={ownerView ? `${title} (clicca per modificare)` : title}
            key={`btn-custom-${idx}`}
          >
            <span>
              <IconButton
                className="action-btn compact"
                size="small"
                aria-label={title}
                sx={{ p: 0.5 }}
                onClick={() =>
                  ownerView ? openCustomButtonDialog(idx) : handleCustomButtonClick(idx)
                }
              >
                {renderCustomButtonIcon(btn.iconKey)}
              </IconButton>
            </span>
          </Tooltip>
        );
      }

      if (showInMenu && btn.url) {
        const groupName = btn.menuId || 'Personalizzati';
        if (!customMenuGroups[groupName]) customMenuGroups[groupName] = [];
        customMenuGroups[groupName].push(
          <MenuItem
            key={`m-custom-${idx}`}
            onClick={() => {
              if (ownerView) {
                openCustomButtonDialog(idx);
              } else {
                handleCustomButtonClick(idx);
              }
              setCustomMenuAnchor(null);
            }}
          >
            <ListItemIcon>{renderCustomButtonIcon(btn.iconKey)}</ListItemIcon>
            <ListItemText primary={title} />
          </MenuItem>
        );
      }
    });

    Object.entries(customMenuGroups).forEach(([groupName, items]) => {
      customMenuItems.push(
        <MenuItem key={`m-custom-group-${groupName}`} disabled>
          <ListItemText primary={groupName} />
        </MenuItem>,
        ...items
      );
    });

    if (ownerView) {
      const title = 'Nuovo pulsante personalizzato';
      btns.push(
        <Tooltip title={title} key="btn-custom-new">
          <span>
            <IconButton
              className="action-btn compact"
              size="small"
              aria-label={title}
              sx={{ p: 0.5 }}
              onClick={() => openCustomButtonDialog(null)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      );

      customMenuItems.push(
        <MenuItem
          key="m-custom-new"
          onClick={() => {
            openCustomButtonDialog(null);
            setCustomMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Aggiungi pulsante personalizzato" />
        </MenuItem>
      );
    }

    if (adminMenuItems.length) {
      btns.unshift(
        <Tooltip title="Admin" key="btn-menu-admin">
          <span>
            <IconButton
              className="action-btn compact"
              size="small"
              aria-label="Menu Admin"
              sx={{ p: 0.5 }}
              onClick={(e) => setAdminMenuAnchor(e.currentTarget)}
            >
              <FixIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      );
    }

    if (linksMenuItems.length) {
      btns.unshift(
        <Tooltip title="Link" key="btn-menu-links">
          <span>
            <IconButton
              className="action-btn compact"
              size="small"
              aria-label="Menu Link"
              sx={{ p: 0.5 }}
              onClick={(e) => setLinksMenuAnchor(e.currentTarget)}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      );
    }

    if (customMenuItems.length) {
      btns.unshift(
        <Tooltip title="Personalizzati" key="btn-menu-custom">
          <span>
            <IconButton
              className="action-btn compact"
              size="small"
              aria-label="Menu Personalizzati"
              sx={{ p: 0.5 }}
              onClick={(e) => setCustomMenuAnchor(e.currentTarget)}
            >
              <StarBorderIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      );
    }

    const uploadInput = (
      <input
        type="file"
        ref={uploadInputRef}
        style={{ display: 'none' }}
        onChange={onUploadFileChange}
        key="hidden-file-input"
      />
    );

    return (
      <div className="sidebar-actions">
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca (nomi, tag)…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* griglia 5 per riga */}
        <Box
          className="action-buttons"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 1,
            alignItems: 'center',
            justifyItems: 'center',
          }}
        >
          {btns}
        </Box>

        {/* Menu Admin */}
        <Menu
          anchorEl={adminMenuAnchor}
          open={Boolean(adminMenuAnchor)}
          onClose={() => setAdminMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {adminMenuItems}
        </Menu>

        {/* Menu Link */}
        <Menu
          anchorEl={linksMenuAnchor}
          open={Boolean(linksMenuAnchor)}
          onClose={() => setLinksMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {linksMenuItems}
        </Menu>

        {/* Menu Personalizzati */}
        <Menu
          anchorEl={customMenuAnchor}
          open={Boolean(customMenuAnchor)}
          onClose={() => setCustomMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {customMenuItems}
        </Menu>

        {uploadInput}
      </div>
    );
  };

  const renderFooter = () => (
    <div className="sidebar-footer">
      <div className="usage-row">
        <LinearProgress variant="determinate" value={usedPct} className="usage-bar" />
        <div className="usage-label">
          {human(usage?.usedBytes || 0)} / {human(STORAGE_LIMIT_BYTES)}
        </div>
      </div>
      <Divider className="divider" />
      <ListItem button onClick={() => { auth.signOut(); handleClose(); }} className="sidebar-item">
        <ListItemIcon className="sidebar-icon">
          <Logout />
        </ListItemIcon>
        <ListItemText primary={<Typography sx={{ ...oneLine }}>Esci</Typography>} />
      </ListItem>
    </div>
  );

  /* -------------------- RENDER -------------------- */
  void navigate; // avoid unused warning

  return (
    <Drawer
      anchor="left"
      variant="temporary"
      open={isOpen}
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      classes={{ paper: 'sidebar-drawer-paper' }}
      PaperProps={{
        sx: {
          height: '100dvh',
          maxHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <div
        className="sidebar-root"
        role="presentation"
        style={{
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {renderHeader()}

        <div
          className="sidebar-content"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {renderSearchAndActions()}

          <Divider className="divider" />

          <List className="sidebar-list">
            {mixedItems.map((item) => {
              if (item.type === 'orphans') return renderOrphans();
              if (item.type === 'static') return renderStaticBlock(item.data);
              if (item.type === 'folder') return renderCustomFolder(item.data);
              return null;
            })}
          </List>
        </div>

        {renderFooter()}
      </div>

      {/* MENU */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {(menuTarget?.type === 'file' ||
          menuTarget?.type === 'folder' ||
          menuTarget?.type === 'static' ||
          menuTarget?.type === 'static-sub') && (
          <MenuItem onClick={() => openEditItem(menuTarget)}>
            <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Modifica…
          </MenuItem>
        )}

        {menuTarget?.type === 'file' && (
          <MenuItem onClick={askMove}>
            <MoveIcon fontSize="small" style={{ marginRight: 8 }} /> Sposta…
          </MenuItem>
        )}

        {menuTarget?.type === 'file' && (
          <MenuItem onClick={askTags}>
            <LabelIcon fontSize="small" style={{ marginRight: 8 }} /> Tag…
          </MenuItem>
        )}

        {menuTarget?.type === 'file' && (
          <MenuItem onClick={async () => {
            try {
              const f = menuTarget?.data;
              if (!f) return;
              const url = f.downloadURL || (await getDownloadURLByPath(f.storagePath));
              await navigator.clipboard.writeText(url);
              setSnack({ open: true, type: 'success', msg: 'Link scaricabile copiato' });
              closeMenu();
            } catch (e) {
              console.error(e);
              setSnack({ open: true, type: 'error', msg: 'Impossibile copiare il link' });
              closeMenu();
            }
          }}>
            <OpenInNew fontSize="small" style={{ marginRight: 8 }} /> Copia link scaricabile
          </MenuItem>
        )}

        {(menuTarget?.type === 'file' || menuTarget?.type === 'folder') && (
          <MenuItem onClick={doDelete} sx={{ color: 'error.main' }}>
            <DeleteOutline fontSize="small" style={{ marginRight: 8 }} /> Elimina
          </MenuItem>
        )}
      </Menu>

      {/* DIALOG: MODIFICA */}
      <Dialog open={dlgEditItemOpen} onClose={() => setDlgEditItemOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Modifica</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={
              dlgEditItemData.type === 'file'
                ? 'Nome file'
                : dlgEditItemData.type === 'folder'
                ? 'Nome cartella'
                : 'Titolo link'
            }
            value={dlgEditItemData.label}
            onChange={(e) => setDlgEditItemData((p) => ({ ...p, label: e.target.value }))}
            sx={{ mt: 1, mb: 2 }}
          />

          {/* Icona visiva solo per CARTELLA */}
          {dlgEditItemData.type === 'folder' && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Icona cartella</Typography>
              <IconPicker
                value={dlgEditItemData.iconKey || 'folder'}
                onChange={(val) => setDlgEditItemData((p) => ({ ...p, iconKey: val }))}
              />
            </>
          )}

          {(dlgEditItemData.type === 'file' || dlgEditItemData.type === 'folder' || dlgEditItemData.type === 'static' || dlgEditItemData.type === 'static-sub') && (
            <FolderSelect
              label="Sposta in cartella"
              value={dlgEditItemData.moveTo ?? ''}
              onChange={(val) => setDlgEditItemData((p) => ({ ...p, moveTo: val || '' }))}
              excludeId={dlgEditItemData.type === 'folder' ? dlgEditItemData.id : undefined}
            />
          )}

          <TextField
            select
            fullWidth
            label="Visibilità"
            value={dlgEditItemData.visibility || 'public'}
            onChange={(e) => setDlgEditItemData((p) => ({ ...p, visibility: e.target.value }))}
            SelectProps={{ native: false }}
          >
            <MenuItem value="private">Privata (solo tu)</MenuItem>
            <MenuItem value="public">Pubblica (tutti)</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgEditItemOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={submitEditItem}>Salva</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: SPOSTA rapido (file) */}
      <Dialog open={dlgMoveOpen} onClose={() => setDlgMoveOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Sposta in…</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Filtra cartelle…"
            value={moveFilter}
            onChange={(e) => setMoveFilter(e.target.value)}
            size="small"
            sx={{ mb: 1 }}
          />
          <List dense>
            {customFolders
              .filter((f) => !moveFilter || matchQuery(f.label, moveFilter))
              .sort(sortFolders)
              .map((f) => (
                <ListItem key={f.id} button onClick={() => doMove(f.id)} sx={{ minHeight: 36 }}>
                  <ListItemIcon>
                    <span className="folder-icon-box">
                      {renderFolderIconByKey(f.iconKey)}
                    </span>
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ ...oneLine }}>{f.label}</Typography>}
                  />
                </ListItem>
              ))}
            {/* statiche come destinazione rapida */}
            {staticFolderTargets
              .filter((sf) => !moveFilter || matchQuery(sf.title, moveFilter))
              .map((sf) => (
                <ListItem key={`static:${sf.id}`} button onClick={() => doMove(`static:${sf.id}`)} sx={{ minHeight: 36 }}>
                  <ListItemIcon>
                    <span className="folder-icon-box">
                      <OpenInNew fontSize="small" />
                    </span>
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ ...oneLine }}>{sf.title}</Typography>}
                  />
                </ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgMoveOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: TAG (file) */}
      <Dialog open={dlgTagsOpen} onClose={() => setDlgTagsOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Tag del file</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="es. Avvento, Genitori, PDF"
            helperText="Separa i tag con virgole"
            value={dlgTagsValue}
            onChange={(e) => setDlgTagsValue(e.target.value)}
            onKeyDown={(e) => (e.key === 'Enter' ? doSaveTags() : undefined)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgTagsOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={doSaveTags}>Salva</Button>
        </DialogActions>
      </Dialog>


      {/* DIALOG: UPLOAD FILE */}
      <Dialog
        open={dlgUploadOpen}
        onClose={() => {
          if (!busy) {
            setDlgUploadOpen(false);
            setUploadFile(null);
            if (uploadInputRef.current) uploadInputRef.current.value = '';
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Carica file</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Seleziona il file da caricare e la cartella di destinazione. Puoi creare anche una nuova cartella.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              File
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={onPickUploadFile}
                disabled={busy}
              >
                Scegli file…
              </Button>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {uploadFile ? (uploadFile.name || 'File selezionato') : 'Nessun file selezionato'}
              </Typography>
            </Box>
          </Box>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Destinazione
            </Typography>
            <RadioGroup
              value={uploadMode}
              onChange={(e) => setUploadMode(e.target.value)}
            >
              <FormControlLabel
                value="existing"
                control={<Radio />}
                label="Usa cartella esistente"
              />
              <FormControlLabel
                value="new"
                control={<Radio />}
                label="Crea nuova cartella"
              />
            </RadioGroup>
          </FormControl>

          {uploadMode === 'existing' && (
            <FolderSelect
              label="Cartella di destinazione"
              value={uploadSelectedFolder}
              onChange={setUploadSelectedFolder}
            />
          )}

          {uploadMode === 'new' && (
            <>
              <TextField
                fullWidth
                label="Nome nuova cartella"
                value={uploadNewFolderName}
                onChange={(e) => setUploadNewFolderName(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Icona nuova cartella
              </Typography>
              <IconPicker
                value={uploadNewFolderIconKey}
                onChange={setUploadNewFolderIconKey}
              />

              <TextField
                select
                fullWidth
                label="Visibilità nuova cartella"
                value={uploadNewFolderVisibility}
                onChange={(e) => setUploadNewFolderVisibility(e.target.value)}
                SelectProps={{ native: false }}
                sx={{ mt: 1 }}
              >
                <MenuItem value="public">Pubblica (tutti)</MenuItem>
                <MenuItem value="private">Privata (solo tu)</MenuItem>
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDlgUploadOpen(false);
              setUploadFile(null);
              if (uploadInputRef.current) uploadInputRef.current.value = '';
            }}
            disabled={busy}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={onConfirmUpload}
            disabled={busy || !uploadFile}
          >
            Carica
          </Button>
        </DialogActions>
      </Dialog>
      {/* DIALOG: NUOVA CARTELLA */}
      <Dialog open={dlgNewFolderOpen} onClose={() => setDlgNewFolderOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Nuova cartella</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome cartella"
            value={dlgNewFolderData.label}
            onChange={(e) => setDlgNewFolderData((p) => ({ ...p, label: e.target.value }))}
            sx={{ mt: 1, mb: 2 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Icona</Typography>
          <IconPicker
            value={dlgNewFolderData.iconKey}
            onChange={(val) => setDlgNewFolderData((p) => ({ ...p, iconKey: val }))}
          />

          <TextField
            select
            fullWidth
            label="Visibilità"
            value={dlgNewFolderData.visibility}
            onChange={(e) => setDlgNewFolderData((p) => ({ ...p, visibility: e.target.value }))}
            SelectProps={{ native: false }}
            sx={{ mt: 1 }}
          >
            <MenuItem value="public">Pubblica (tutti)</MenuItem>
            <MenuItem value="private">Privata (solo tu)</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgNewFolderOpen(false)}>Chiudi</Button>
          <Button
            variant="contained"
            disabled={!dlgNewFolderData.label}
            onClick={async () => {
              try {
                setBusy(true);
                await createCustomFolder({
                  label: dlgNewFolderData.label.trim(),
                  iconKey: dlgNewFolderData.iconKey || 'folder',
                  visibility: dlgNewFolderData.visibility || 'public',
                  parentId: null,
                });
                await refreshFolders();
                setDlgNewFolderOpen(false);
                setSnack({ open: true, type: 'success', msg: 'Cartella creata' });
              } catch (e) {
                console.error(e);
                setSnack({ open: true, type: 'error', msg: 'Errore creazione cartella' });
              } finally {
                setBusy(false);
              }
            }}
          >
            Crea
          </Button>
        </DialogActions>
      </Dialog>

      {/* APPROVAZIONI */}
      <Dialog open={dlgApprovalsOpen} onClose={() => setDlgApprovalsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Richieste di approvazione</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Gestisci qui le richieste di accesso al Diario di bordo.
            Puoi approvare o rifiutare gli utenti che hanno chiesto di collegarsi alla tua parrocchia.
          </Typography>

          

          {approvalsLoading ? (
            <div style={{ padding: 12 }}><LinearProgress /></div>
          ) : pendingApprovals.length === 0 ? (
            <Alert severity="info">Nessuna richiesta in attesa.</Alert>
          ) : (
            <List dense>
              {pendingApprovals.map((u) => (
                <ListItem key={u.id} secondaryAction={
                  <Button size="small" variant="contained" onClick={() => approveUser(u.id)} disabled={approvalsLoading}>
                    Approva
                  </Button>
                }>
                  <ListItemText primary={u.displayName || u.email || u.id} secondary={u.email ? `Email: ${u.email}` : u.id} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgApprovalsOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: PROMEMORIA APPBAR */}
      <Dialog open={dlgAppbarHighlightOpen} onClose={() => setDlgAppbarHighlightOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Promemoria barra superiore</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Il testo configurato qui verrà mostrato nella barra superiore della Dashboard,
            accanto alla data del giorno. Puoi usare la modalità automatica per tempo liturgico
            (Avvento, Natale, Capodanno, Epifania) oppure inserire un testo manuale.
          </Typography>

          <Box
            sx={{
              mb: 2,
              p: 1,
              borderRadius: 1,
              border: (theme) => `1px dashed ${theme.palette.divider}`,
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.background.default
                  : theme.palette.grey[50],
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Anteprima frasi automatiche (tempo liturgico)
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              Elenco dei promemoria che verranno usati in modalità <strong>Automatica</strong>,
              dal periodo attuale fino all&apos;anno liturgico successivo.
            </Typography>
            <List
              dense
              sx={{
                maxHeight: 220,
                overflowY: 'auto',
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: 'background.paper',
              }}
            >
              {liturgicalPreview.map((item) => (
                <ListItem key={`${item.dateLabel}-${item.text}`} sx={{ py: 0.25 }}>
                  <ListItemText
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { display: 'flex', alignItems: 'center', gap: 1 },
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'text.secondary',
                    }}
                    primary={item.text}
                    secondary={item.dateLabel}
                  />
                </ListItem>
              ))}
              {liturgicalPreview.length === 0 && (
                <ListItem>
                  <ListItemText
                    primaryTypographyProps={{ variant: 'body2' }}
                    primary="Nessuna anteprima disponibile al momento."
                    secondary="Verifica che l’orologio del dispositivo sia impostato correttamente."
                    secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  />
                </ListItem>
              )}
            </List>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Modalità</Typography>
          <RadioGroup
            row
            value={appbarHighlightMode}
            onChange={(e) => setAppbarHighlightMode(e.target.value)}
            sx={{ mb: 2 }}
          >
            <FormControlLabel value="auto" control={<Radio />} label="Automatica (tempo liturgico)" />
            <FormControlLabel value="manual" control={<Radio />} label="Manuale" />
            <FormControlLabel value="off" control={<Radio />} label="Nessun promemoria" />
          </RadioGroup>

          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            label="Testo manuale (opzionale)"
            value={appbarHighlightManualText}
            onChange={(e) => setAppbarHighlightManualText(e.target.value)}
            disabled={appbarHighlightMode !== 'manual'}
            helperText="Esempio: Incontro con i genitori alle 18:30, Festa di inizio anno, Uscita con i ragazzi…"
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Icona per il promemoria (solo modalità manuale)
            </Typography>
            <AppbarIconPicker
              value={appbarHighlightManualIconKey}
              onChange={setAppbarHighlightManualIconKey}
            />
            <Typography variant="caption" color="text.secondary">
              L&apos;icona verrà mostrata accanto al testo del promemoria sulla Dashboard.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgAppbarHighlightOpen(false)}>Chiudi</Button>
          <Button
            variant="contained"
            onClick={handleAppbarHighlightSave}
            disabled={!canEditAppbarHighlight || busy}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* NOTE */}
      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>I miei appunti</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={6} value={notesText} onChange={(e) => setNotesText(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Chiudi</Button>
          <Button
            variant="contained"
            disabled={!user || !notesText || notesBusy}
            onClick={async () => {
              try {
                setNotesBusy(true);
                await setUserNotes(user.uid, { text: notesText });
                setSnack({ open: true, type: 'success', msg: 'Appunti salvati' });
                setNotesOpen(false);
              } catch (e) {
                console.error(e);
                setSnack({ open: true, type: 'error', msg: 'Errore salvataggio appunti' });
              } finally {
                setNotesBusy(false);
              }
            }}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: PULSANTI PERSONALIZZATI */}
      <Dialog
        open={customBtnDialogOpen}
        onClose={() => {
          setCustomBtnDialogOpen(false);
          setEditingCustomIndex(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {editingCustomIndex != null
            ? `Configura pulsante ${editingCustomIndex + 1}`
            : 'Configura pulsante'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Etichetta pulsante"
            value={customBtnForm.label}
            onChange={(e) =>
              setCustomBtnForm((prev) => ({ ...prev, label: e.target.value }))
            }
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="URL destinazione"
            helperText="Inserisci un link completo (es. https://...)"
            value={customBtnForm.url}
            onChange={(e) =>
              setCustomBtnForm((prev) => ({ ...prev, url: e.target.value }))
            }
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Icona
          </Typography>
          <CustomButtonIconPicker
            value={customBtnForm.iconKey}
            onChange={(val) =>
              setCustomBtnForm((prev) => ({ ...prev, iconKey: val }))
            }
          />

          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Posizione
          </Typography>
          <RadioGroup
            row
            value={customBtnForm.placement || 'both'}
            onChange={(e) =>
              setCustomBtnForm((prev) => ({ ...prev, placement: e.target.value }))
            }
          >
            <FormControlLabel value="inline" control={<Radio />} label="Solo in alto" />
            <FormControlLabel value="menu" control={<Radio />} label="Solo nel menu" />
            <FormControlLabel value="both" control={<Radio />} label="Entrambi" />
          </RadioGroup>

          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Visibilità
          </Typography>
          <RadioGroup
            row
            value={customBtnForm.visibility || 'all'}
            onChange={(e) =>
              setCustomBtnForm((prev) => ({ ...prev, visibility: e.target.value }))
            }
          >
            <FormControlLabel value="all" control={<Radio />} label="Tutti" />
            <FormControlLabel value="admin" control={<Radio />} label="Solo admin/owner" />
            <FormControlLabel value="owner" control={<Radio />} label="Solo owner" />
          </RadioGroup>

          <TextField
            fullWidth
            label="Email abilitate (separate da virgola)"
            helperText="Lascia vuoto per usare solo la regola di visibilità sopra."
            value={customBtnForm.visibilityEmails || ''}
            onChange={(e) =>
              setCustomBtnForm((prev) => ({
                ...prev,
                visibilityEmails: e.target.value,
              }))
            }
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth
            label="Nome menu"
            helperText="Es. Personalizzati, Catechisti, Liturgia…"
            value={customBtnForm.menuId || 'Personalizzati'}
            onChange={(e) =>
              setCustomBtnForm((prev) => ({
                ...prev,
                menuId: e.target.value || 'Personalizzati',
              }))
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          {editingCustomIndex != null && (
            <Button color="error" onClick={handleCustomButtonDelete}>
              Elimina
            </Button>
          )}
          <Button
            onClick={() => {
              setCustomBtnDialogOpen(false);
              setEditingCustomIndex(null);
            }}
          >
            Annulla
          </Button>
          <Button onClick={handleCustomButtonTestOpen} disabled={!customBtnForm.url}>
            Apri link
          </Button>
          <Button variant="contained" onClick={handleCustomButtonSave}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: CONFIGURAZIONE PULSANTI HEADER */}
      <Dialog
        open={headerButtonsConfigDialogOpen}
        onClose={() => setHeaderButtonsConfigDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Configura pulsanti header</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Solo il proprietario può modificare queste opzioni. Qui scegli etichette, icone,
            visibilità, email abilitate e posizione (inline/menu) dei pulsanti di
            amministrazione e dei link fissi.
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="header-btn-select-label">Pulsante</InputLabel>
            <Select
              labelId="header-btn-select-label"
              label="Pulsante"
              value={selectedHeaderButtonId}
              onChange={(e) => setSelectedHeaderButtonId(e.target.value)}
            >
              {HEADER_BUTTON_ORDER.map((id) => {
                const fallbackLabel =
                  (HEADER_BUTTON_DEFAULTS[id] && HEADER_BUTTON_DEFAULTS[id].label) || id;
                return (
                  <MenuItem key={id} value={id}>
                    {fallbackLabel}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {(() => {
            const id = selectedHeaderButtonId;
            const cfg = getHeaderButtonConfig(headerButtonsConfigDraft, id);
            const fallbackLabel =
              (HEADER_BUTTON_DEFAULTS[id] && HEADER_BUTTON_DEFAULTS[id].label) || id;

            return (
              <Box
                key={id}
                sx={{
                  mb: 2,
                  pb: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {fallbackLabel}
                </Typography>

                <TextField
                  fullWidth
                  label="Etichetta pulsante"
                  value={cfg.label || ''}
                  onChange={(e) =>
                    handleHeaderButtonConfigFieldChange(id, 'label', e.target.value)
                  }
                  sx={{ mb: 1 }}
                />

                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                  Icona
                </Typography>
                <CustomButtonIconPicker
                  value={cfg.iconKey}
                  onChange={(val) =>
                    handleHeaderButtonConfigFieldChange(id, 'iconKey', val)
                  }
                />

                {/* >>> UNICA MODIFICA: campo URL solo per 'foglietto' <<< */}
                {id === 'foglietto' && (
                  <TextField
                    fullWidth
                    label="URL foglietto"
                    placeholder="https://…"
                    value={cfg.url || ''}
                    onChange={(e) =>
                      handleHeaderButtonConfigFieldChange(id, 'url', e.target.value)
                    }
                    sx={{ mb: 2 }}
                    inputProps={{ spellCheck: 'false' }}
                    helperText="Lascia vuoto per usare l’URL predefinito."
                  />
                )}

                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption">Posizione</Typography>
                  <RadioGroup
                    row
                    value={cfg.placement || 'both'}
                    onChange={(e) =>
                      handleHeaderButtonConfigFieldChange(id, 'placement', e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="inline"
                      control={<Radio />}
                      label="Solo in alto"
                    />
                    <FormControlLabel
                      value="menu"
                      control={<Radio />}
                      label="Solo nel menu"
                    />
                    <FormControlLabel
                      value="both"
                      control={<Radio />}
                      label="Entrambi"
                    />
                  </RadioGroup>
                </Box>

                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption">Visibilità</Typography>
                  <RadioGroup
                    row
                    value={cfg.visibility || 'all'}
                    onChange={(e) =>
                      handleHeaderButtonConfigFieldChange(id, 'visibility', e.target.value)
                    }
                  >
                    <FormControlLabel value="all" control={<Radio />} label="Tutti" />
                    <FormControlLabel
                      value="admin"
                      control={<Radio />}
                      label="Solo admin/owner"
                    />
                    <FormControlLabel
                      value="owner"
                      control={<Radio />}
                      label="Solo owner"
                    />
                  </RadioGroup>
                </Box>

                <TextField
                  fullWidth
                  label="Email abilitate (separate da virgola)"
                  helperText="Se compilato, il pulsante è visibile solo a queste email, oltre alle regole di visibilità sopra."
                  value={cfg.visibilityEmails || ''}
                  onChange={(e) =>
                    handleHeaderButtonConfigFieldChange(id, 'visibilityEmails', e.target.value)
                  }
                  sx={{ mt: 2 }}
                />
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHeaderButtonsConfigDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleHeaderButtonsConfigSave}>
            Salva configurazione
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: RESET PRIVACY UTENTE */}
      <Dialog
        open={privacyResetDialogOpen}
        onClose={() => !privacyResetBusy && setPrivacyResetDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Reset privacy utente</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Inserisci l&apos;email dell&apos;utente a cui vuoi far ricomparire la finestra di consenso.
          </Typography>
          <TextField
            fullWidth
            type="email"
            label="Email utente"
            value={privacyResetEmail}
            onChange={(e) => setPrivacyResetEmail(e.target.value)}
            disabled={privacyResetBusy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrivacyResetDialogOpen(false)} disabled={privacyResetBusy}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleResetPrivacyForEmail}
            disabled={privacyResetBusy || !privacyResetEmail.trim()}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: EDITOR PRIVACY POLICY */}
      <Dialog
        open={privacyEditorOpen}
        onClose={() => setPrivacyEditorOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Editor Privacy</DialogTitle>
        <DialogContent dividers>
          {privacyLoading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
            </Box>
          )}

          <TextField
            fullWidth
            label="Versione (es. v1.0 - 01/2025)"
            value={privacyVersion}
            onChange={(e) => setPrivacyVersion(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />

          <TextField
            fullWidth
            multiline
            minRows={10}
            label="Testo informativa privacy"
            value={privacyText}
            onChange={(e) => setPrivacyText(e.target.value)}
            inputRef={privacyTextareaRef}
          />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            <Button size="small" onClick={() => wrapSelection('**', '**')}>
              Grassetto
            </Button>
            <Button size="small" onClick={() => wrapSelection('_', '_')}>
              Corsivo
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrivacyEditorOpen(false)}>Chiudi</Button>
          <Button
            onClick={handleSavePrivacy}
            variant="contained"
            disabled={privacyLoading}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>


      {/* DIALOG: BACKUP & RIPRISTINO */}
      <Dialog
        open={backupDialogOpen}
        onClose={() => !backupBusy && setBackupDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Backup &amp; Ripristino</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Il sistema esegue un backup automatico giornaliero su Cloud Storage. Da qui puoi creare
            un backup manuale completo, scaricarlo sul tuo PC e ripristinare un backup esistente.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Spazio utilizzato (totale Storage)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress variant="determinate" value={usedPct} />
              </Box>
              <Typography variant="caption">
                {human(usage?.usedBytes || 0)} / {human(STORAGE_LIMIT_BYTES)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleManualBackup}
              disabled={backupBusy || !user}
            >
              Esegui backup completo ora
            </Button>
            {!user && (
              <Typography variant="caption">
                Devi essere autenticato per creare un backup.
              </Typography>
            )}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Backup disponibili
          </Typography>
          {backups.length === 0 ? (
            <Alert severity="info">Nessun backup registrato al momento.</Alert>
          ) : (
            <List dense>
              {backups.map((b) => {
                let when = '';
                if (b.createdAt) {
                  const d = b.createdAt.toDate
                    ? b.createdAt.toDate()
                    : new Date(
                        b.createdAt.seconds
                          ? b.createdAt.seconds * 1000
                          : b.createdAt
                      );
                  if (!Number.isNaN(d.getTime())) {
                    when = d.toLocaleString('it-IT');
                  }
                }
                return (
                  <ListItem
                    key={b.id}
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDownloadBackup(b)}
                          disabled={backupBusy}
                        >
                          Scarica
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="contained"
                          onClick={() => handleRestoreBackup(b)}
                          disabled={backupBusy}
                        >
                          Ripristina
                        </Button>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={`${when || 'Data sconosciuta'} — ${
                        b.type === 'auto' ? 'Automatico' : 'Manuale'
                      }`}
                      secondary={
                        <>
                          {b.size ? `Dimensione: ${human(b.size)}` : ''}
                          {b.note ? ` — Note: ${b.note}` : ''}
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupDialogOpen(false)} disabled={backupBusy}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <ClassStudentsModal
        open={classStudentsOpen}
        onClose={() => setClassStudentsOpen(false)}
      />

      {/* SNACK */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.type || 'success'} onClose={() => setSnack((s) => ({ ...s, open: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </Drawer>
  );
}